/**
 * Las reglas del taller, separadas de la UI.
 *
 * POR QUE ESTE ARCHIVO EXISTE
 * ---------------------------
 * El taller tiene una maquina de estados y esa maquina VIVE EN LA BASE:
 * `transicion_ot_valida()` decide que movimiento se puede hacer desde donde, y
 * `cambiar_estado_ot()` aplica los efectos de cada uno (descontar repuestos,
 * calcular la garantia, borrar la clave del equipo). El frontend no puede tener
 * una segunda version de esa tabla: si difieren, la pantalla ofrece botones que
 * el backend rechaza, y el operador aprende que el sistema miente.
 *
 * Por eso `TRANSICIONES` es una copia LITERAL del `case` de
 * `0008_reparaciones.sql`, con los mismos nueve estados y las mismas salidas.
 * Cualquier cambio en el SQL se copia aca y se prueba en `taller.test.tsx`.
 *
 * Ninguna funcion de este archivo toca React ni el DOM.
 */
import {
  convertir,
  formatear,
  formatearCotizacionNumero,
  redondear,
  type Moneda,
} from "@tora/shared";

import {
  aNumero,
  type CobroDiagnostico,
  type CostosOt,
  type EntregaOt,
  type EstadoOt,
  type MedioPago,
  type MotivoCierreOt,
  type NuevaOrdenReparacion,
  type Numerico,
  type OrdenReparacion,
  type Producto,
  type ResultadoCostosOt,
  type TableroTaller,
  type UsadoEnTaller,
} from "@/lib/tipos";

// ─────────────────────────────────────────────────────────────────────────────
// La forma real que devuelve el backend
// ─────────────────────────────────────────────────────────────────────────────

/**
 * `v_ordenes_reparacion` tal como responde de verdad.
 *
 * `lib/tipos.OrdenReparacion` modela la tabla cruda; la VISTA trae ademas los
 * dias en taller ya calculados por Postgres, el presupuesto, el comprobante con
 * el que se facturo y los totales de repuestos. Se extiende aca con esos campos
 * OPCIONALES en vez de tocar `lib/`: asi una `OrdenReparacion` entra en una
 * `OrdenTaller` sin un solo `as`.
 *
 * Lo que la vista NO trae, y por eso la ficha no lo muestra: `color`,
 * `accesorios`, `password_equipo` (dato sensible, restringido por la RLS de
 * 0009) y las marcas de tiempo `presupuesto_at` / `aprobado_at` /
 * `rechazado_at`.
 */
export interface OrdenTaller extends OrdenReparacion {
  /** La parte secreta del link al talón (`/t/:token`, 0088). */
  token_publico?: string | null;
  diagnostico?: string | null;
  presupuesto_importe?: Numerico | null;
  presupuesto_moneda?: Moneda | null;
  garantia_hasta?: string | null;
  fecha_entrega?: string | null;
  /** Numero del comprobante emitido. Si no es `null`, la orden ya se facturo. */
  comprobante?: string | null;
  facturado?: Numerico | null;
  moneda_facturado?: Moneda | null;
  /**
   * El estado del comprobante (0131): EMITIDO o ANULADO. Sin esto la ficha
   * restaba lo cobrado de una venta anulada y decia «Queda en cuenta».
   */
  estado_comprobante?: string | null;
  /**
   * Los dias que el equipo estuvo (o esta) en el local, calculados por la base:
   * hasta hoy mientras sigue aca, hasta el dia en que se fue una vez cerrada
   * (0131). Antes contaban hasta hoy siempre, y una entregada en el dia decia
   * «22 días».
   */
  dias_en_taller?: Numerico | null;
  costo_repuestos_ars?: Numerico | null;
  precio_repuestos_ars?: Numerico | null;
  repuestos?: Numerico | null;
  accesorios?: string | null;
  /** Con que encomienda viaja el equipo al servicio tecnico. */
  transporte?: string | null;
  /** A que servicio tecnico externo se mando. */
  tercerizado?: string | null;
  /**
   * Si hay cuenta, contrasena o patron cargados. La vista dice QUE hay, no
   * cuales: esos se piden aparte, de a uno, por `useAccesosOt()`.
   */
  tiene_accesos?: boolean | null;
  /** Los tres montos (0032). `costos_at` en null es "todavia no contesto el tecnico". */
  encomienda_importe?: Numerico | null;
  costo_tecnico_importe?: Numerico | null;
  ganancia_importe?: Numerico | null;
  costos_moneda?: Moneda | null;
  costos_at?: string | null;
  /** La suma de los tres, calculada por la base. */
  precio_cliente?: Numerico | null;
  /** Las señas, en las dos monedas congeladas el dia que entraron. */
  senas_ars?: Numerico | null;
  senas_usd?: Numerico | null;
  senas?: Numerico | null;
  pagado_facturado?: Numerico | null;
  /**
   * El diagnostico (0039): lo que se cobra para que el equipo entre a la mesa.
   * Es pago a cuenta del total, asi que ya viene sumado en `senas_*`.
   * `diagnostico_motivo` con texto significa que NO se cobro, y por que.
   */
  diagnostico_importe?: Numerico | null;
  diagnostico_moneda?: Moneda | null;
  diagnostico_motivo?: string | null;
  /** Cuando se le pago al tecnico. En null: el egreso todavia no se asento. */
  pago_tecnico_at?: string | null;
  egresos_ars?: Numerico | null;
  egresos_usd?: Numerico | null;
  /**
   * Lo que se le devolvio al cliente de una orden anulada (`devolver_sena_ot`,
   * 0121_9), en las dos monedas congeladas. Sin esto la ficha seguia diciendo
   * «Quedó cobrado» despues de devolverlo.
   */
  devuelto_ars?: Numerico | null;
  devuelto_usd?: Numerico | null;
  /**
   * Lo que queda por devolver de una anulada, moneda por moneda
   * (`sena_por_devolver_ot`, 0121_9): `sena_queda_*` es lo que queda de lo que
   * entro EN esa moneda, y `devolver_tope_*` todo lo que se puede devolver
   * desde una caja de esa moneda (el otro pozo convertido a la cotizacion en
   * que entro). En null si la orden no esta anulada.
   */
  sena_queda_ars?: Numerico | null;
  sena_queda_usd?: Numerico | null;
  devolver_tope_ars?: Numerico | null;
  devolver_tope_usd?: Numerico | null;
  /** Por que volvio el equipo, incluso despues de entregado. */
  motivo_cierre?: MotivoCierreOt | null;
  /** Orden interna (0048): el usado del stock que se manda a reacondicionar. */
  producto_id?: number | null;
  producto?: string | null;
  producto_imei?: string | null;
  es_interna?: boolean | null;
}

// ─────────────────────────────────────────────────────────────────────────────
// La maquina de estados
// ─────────────────────────────────────────────────────────────────────────────

/** Los nueve estados, en el orden en que los declara el `check` de la tabla. */
export const ESTADOS_OT: readonly EstadoOt[] = [
  "RECIBIDO",
  "DIAGNOSTICO",
  "PRESUPUESTADO",
  "APROBADO",
  "EN_REPARACION",
  "LISTO",
  "ENTREGADO",
  "SIN_ARREGLO",
  "RECHAZADO",
  "ANULADO",
];

/**
 * El camino normal de un equipo. `SIN_ARREGLO` y `ANULADO` quedan afuera a
 * proposito: no son etapas, son salidas.
 */
export const FLUJO: readonly EstadoOt[] = [
  "RECIBIDO",
  "DIAGNOSTICO",
  "PRESUPUESTADO",
  "APROBADO",
  "EN_REPARACION",
  "LISTO",
  "ENTREGADO",
];

/**
 * Copia literal de `transicion_ot_valida(p_desde, p_hasta)`.
 *
 * Leer las filas de arriba abajo es leer el taller entero:
 *  · de RECIBIDO se puede saltar el diagnostico y presupuestar derecho, porque
 *    a veces la falla se ve de una;
 *  · de EN_REPARACION ya no se puede anular: el stock salio;
 *  · de LISTO se puede VOLVER a EN_REPARACION, que es lo que pasa cuando el
 *    cliente lo prueba en el mostrador y algo sigue fallando;
 *  · ENTREGADO y ANULADO no tienen salida: son terminales.
 */
export const TRANSICIONES: Record<EstadoOt, readonly EstadoOt[]> = {
  RECIBIDO: ["DIAGNOSTICO", "PRESUPUESTADO", "SIN_ARREGLO", "ANULADO"],
  DIAGNOSTICO: ["PRESUPUESTADO", "SIN_ARREGLO", "ANULADO"],
  PRESUPUESTADO: ["APROBADO", "RECHAZADO", "SIN_ARREGLO", "ANULADO"],
  APROBADO: ["EN_REPARACION", "SIN_ARREGLO", "ANULADO"],
  EN_REPARACION: ["LISTO", "SIN_ARREGLO"],
  LISTO: ["ENTREGADO", "EN_REPARACION"],
  ENTREGADO: [],
  SIN_ARREGLO: ["ENTREGADO", "ANULADO"],
  // Vuelve a APROBADO porque pasa: el cliente dice que no y a los dos dias
  // llama que si. Sin esta salida habria que dar de alta la orden de nuevo.
  RECHAZADO: ["APROBADO", "ENTREGADO", "ANULADO"],
  ANULADO: [],
};

/**
 * Los movimientos posibles desde este estado.
 *
 * La pantalla ofrece SOLO esto. La alternativa —mostrar los nueve estados y
 * dejar que la base rechace— convierte cada error de carga en un mensaje rojo
 * despues del clic, y encima con el equipo ya prometido para las cinco.
 */
export function transicionesValidas(estado: EstadoOt): readonly EstadoOt[] {
  return TRANSICIONES[estado] ?? [];
}

export function esTransicionValida(desde: EstadoOt, hasta: EstadoOt): boolean {
  return transicionesValidas(desde).includes(hasta);
}

/** Un estado del que ya no se sale. La orden esta cerrada. */
export function esTerminal(estado: EstadoOt): boolean {
  return transicionesValidas(estado).length === 0;
}

export const ETIQUETA_ESTADO: Record<EstadoOt, string> = {
  RECIBIDO: "Recibido",
  DIAGNOSTICO: "En diagnóstico",
  PRESUPUESTADO: "Presupuestado",
  APROBADO: "Aprobado",
  EN_REPARACION: "En reparación",
  LISTO: "Listo",
  ENTREGADO: "Entregado",
  SIN_ARREGLO: "Sin arreglo",
  RECHAZADO: "Rechazado",
  ANULADO: "Anulado",
};

/** El tono del badge de cada estado (MASTER.md seccion 10). */
/**
 * Sin `outline`: esa variante es para etiquetas ("Stock"), no para estados
 * (MASTER seccion 10). Los terminales neutros van en `secondary`.
 */
export type TonoEstado = "info" | "warning" | "success" | "secondary" | "destructive";

export const TONO_ESTADO: Record<EstadoOt, TonoEstado> = {
  RECIBIDO: "info",
  DIAGNOSTICO: "info",
  PRESUPUESTADO: "warning",
  APROBADO: "warning",
  EN_REPARACION: "warning",
  LISTO: "success",
  ENTREGADO: "secondary",
  SIN_ARREGLO: "secondary",
  RECHAZADO: "secondary",
  ANULADO: "destructive",
};

/** Que dice el boton y QUE HACE la base cuando se aprieta. */
export interface AccionEstado {
  /** Texto del boton. Es un verbo: lo que la persona esta por hacer. */
  verbo: string;
  /** Que le pasa al equipo, en una linea. */
  resumen: string;
  /**
   * El efecto lateral que aplica `cambiar_estado_ot()`. Es lo que se lee en el
   * paso de confirmacion, porque es lo que no se puede deshacer con un clic.
   */
  efecto: string;
  /** Pide mas cuidado en la confirmacion: mueve stock o cierra la orden. */
  delicada?: boolean;
}

export const ACCION_ESTADO: Record<EstadoOt, AccionEstado> = {
  RECIBIDO: {
    verbo: "Volver a recibido",
    resumen: "El equipo vuelve al principio del circuito.",
    efecto: "No mueve stock ni plata.",
  },
  DIAGNOSTICO: {
    verbo: "Mandar a diagnóstico",
    resumen: "El equipo pasa a la mesa del técnico.",
    efecto: "No mueve stock ni plata. Solo deja constancia de que se está revisando.",
  },
  PRESUPUESTADO: {
    verbo: "Presupuestar",
    resumen: "Se le pasa el número al cliente y se espera la respuesta.",
    efecto:
      "Guarda el importe, la moneda y la fecha del presupuesto. Sin importe la base rechaza el cambio.",
  },
  APROBADO: {
    verbo: "El cliente aprobó",
    resumen: "El cliente dijo que sí. Todavía no se tocó nada del equipo.",
    efecto: "Registra la aprobación. Los repuestos siguen en el stock, disponibles para vender.",
  },
  EN_REPARACION: {
    verbo: "Empezar la reparación",
    resumen: "El equipo entra al banco de trabajo.",
    efecto:
      "Acá salen los repuestos del stock. Hasta este momento seguían disponibles para vender a otro.",
    delicada: true,
  },
  LISTO: {
    verbo: "Marcar listo",
    resumen: "El trabajo terminó y el equipo espera que lo vengan a buscar.",
    efecto: "Desde acá se puede facturar la orden.",
  },
  ENTREGADO: {
    verbo: "Entregar",
    resumen: "El equipo se va con el cliente.",
    // Sin orden a mano se habla de lo reparado; con la orden, `efectoDe()` dice
    // si lleva garantia o no (solo lo reparado la lleva).
    efecto:
      "Calcula la garantía desde hoy y BORRA la clave del equipo: ya no hace falta guardarla.",
    delicada: true,
  },
  SIN_ARREGLO: {
    verbo: "No tiene arreglo",
    resumen: "El equipo no se puede reparar. Vuelve como vino.",
    // La mitad de la plata la completa `efectoDe()`: depende de si el
    // diagnostico se cobro al recibir, quedo pendiente o no hubo.
    efecto: "Queda registrado por qué volvió.",
    delicada: true,
  },
  RECHAZADO: {
    verbo: "El cliente no lo aprobó",
    resumen: "Tiene arreglo, pero el cliente no quiso pagar el presupuesto.",
    efecto: "Queda separado de «no tiene arreglo»: son las reparaciones que se pierden por precio.",
    delicada: true,
  },
  ANULADO: {
    verbo: "Anular la orden",
    resumen: "La orden se da de baja.",
    // 0131 borra las claves al anular, como al entregar. Anular tambien se usa
    // para una orden mal cargada con el equipo todavia aca: el aviso es para
    // que la clave se copie antes de perderla.
    efecto:
      "Repone al stock cualquier repuesto que ya se haya descontado y BORRA la cuenta, la clave y el patrón del equipo: si hacen falta, copialos antes. No se puede reabrir.",
    delicada: true,
  },
};

/**
 * Que pasa con la plata cuando el equipo vuelve sin repararse, en una frase.
 *
 * Antes eran tres textos fijos que daban por hecho que el diagnostico estaba
 * pago: con el diagnostico pendiente la ficha decia «se devuelve sin cobrar» y
 * el dialogo de entrega, debajo de ese mismo texto, pedia los US$ 20. El cobro
 * era el correcto; los textos lo contradecian. `entregar_orden` factura el
 * diagnostico en los dos casos: lo que cambia es si ya esta cobrado.
 */
export function cobroAlVolver(orden: OrdenTaller): string {
  if (esInterna(orden)) return "El costo del equipo no cambia.";
  const importe = aNumero(orden.diagnostico_importe);
  if (importe <= 0) return "No hubo diagnóstico: se devuelve sin cobrar.";
  const moneda = orden.diagnostico_moneda ?? "USD";

  // Lo cobrado, en la moneda del diagnostico: la misma cuenta que hace
  // `evaluarEntrega()`. Mirando solo `diagnostico_motivo`, un diagnostico
  // cobrado de menos (el alta deja pisar el importe) decia «ya está pago»
  // arriba de un «Falta cobrar US$ 1,13», y uno pendiente que despues cubrio
  // una seña pedia los US$ 20 al lado de «Ya está pago con la seña». Sin las
  // señas cargadas (una orden que no vino de la vista) queda el motivo.
  const sinSenas =
    (orden.senas_usd === undefined || orden.senas_usd === null) &&
    (orden.senas_ars === undefined || orden.senas_ars === null);
  const cobrado = sinSenas
    ? orden.diagnostico_motivo
      ? 0
      : importe
    : aNumero(moneda === "USD" ? orden.senas_usd : orden.senas_ars);
  const falta = redondear2(Math.max(importe - cobrado, 0));

  if (falta <= 0.009) {
    // Lo cobrado de mas (una seña de la reparacion que no se hizo) no se usa
    // en la factura del diagnostico y queda a favor del cliente: antes la
    // frase terminaba en «No hay nada que cobrar» y esa plata no aparecia en
    // ningun lado. Sin cliente `entregar_orden` no asienta nada en cuenta
    // corriente y la plata queda en la caja: decir «a favor del cliente» ahi
    // contradecia el aviso del dialogo de entrega.
    const sobra = redondear2(cobrado - importe);
    if (sobra > 0.01) {
      const conCliente = orden.cliente_id !== null && orden.cliente_id !== undefined;
      const destino = conCliente
        ? "quedan a favor en su cuenta corriente."
        : "se le devuelven de vuelto, en pesos, al entregar.";
      return `Se factura solo el diagnóstico, que ya está pago. Sobran ${formatear(sobra, moneda)} de lo cobrado a cuenta: ${destino}`;
    }
    return "Se factura solo el diagnóstico, que ya está pago. No hay nada que cobrar.";
  }
  if (cobrado > 0.009) {
    return `Se factura el diagnóstico (${formatear(importe, moneda)}): falta cobrar ${formatear(falta, moneda)}.`;
  }
  return `Se cobra el diagnóstico, que quedó pendiente: ${formatear(importe, moneda)}.`;
}

/**
 * El efecto de la transicion para ESTA orden. Los dos cierres suman lo que pasa
 * con el diagnostico, que no es lo mismo en todas (ver `cobroAlVolver()`).
 */
export function efectoDe(destino: EstadoOt, orden: OrdenTaller | null): string {
  const base = ACCION_ESTADO[destino].efecto;
  if (!orden) return base;
  if (destino === "ENTREGADO" && !llevaGarantia(orden)) {
    return "Sale SIN garantía: solo lo reparado la lleva. BORRA la clave del equipo: ya no hace falta guardarla.";
  }
  if (destino === "ANULADO") {
    const plata = plataAlAnular(orden);
    return plata ? `${base} ${plata}` : base;
  }
  if (destino !== "SIN_ARREGLO" && destino !== "RECHAZADO") return base;
  return `${base} ${cobroAlVolver(orden)}`;
}

/**
 * Si la orden se va con garantia: solo lo REPARADO la lleva (decision del
 * dueño). Espeja `cambiar_estado_ot()` (0131): sin arreglo, rechazada o
 * interna salen sin garantia. Antes los 90 dias se daban a cualquier entrega,
 * tambien al equipo que volvia como vino.
 */
export function llevaGarantia(orden: OrdenTaller): boolean {
  if (esInterna(orden)) return false;
  if (orden.motivo_cierre) return false;
  return orden.estado !== "SIN_ARREGLO" && orden.estado !== "RECHAZADO";
}

/**
 * Lo que anular deja con la plata, en una frase, o `null` si no hay plata.
 *
 * Anular no devuelve la seña ni recupera lo pagado al tecnico, y antes no lo
 * decia: el vendedor anulaba creyendo que la caja se acomodaba sola. El dueño
 * decidio que siga sin mover plata sola; lo que cambia es que se avisa, con
 * los numeros, antes de confirmar.
 */
export function plataAlAnular(orden: OrdenTaller): string | null {
  const partes: string[] = [];
  const saldo = saldoDeLaOrden({ ...orden, estado: "ANULADO" });
  if (saldo.cobrado > 0) {
    partes.push(
      `Lo cobrado a cuenta (${formatear(saldo.cobrado, saldo.moneda)}) NO se devuelve solo: queda en la caja` +
        (orden.cliente_id ? " y a favor del cliente" : "") +
        ". Si hay que devolverlo, después de anular está «Devolver la seña» en la ficha.",
    );
  }
  if (orden.pago_tecnico_at && aNumero(orden.costo_tecnico_importe) > 0) {
    const plata = leerPlata(orden);
    partes.push(
      `Al técnico ya se le pagó ${formatear(plata.costoTecnico, plata.moneda)}: anular no lo recupera.`,
    );
  }
  return partes.length > 0 ? partes.join(" ") : null;
}

/**
 * Si se puede devolver lo cobrado: solo de una anulada (la seña de una orden
 * abierta se descuenta al entregar) y mientras quede algo cobrado.
 */
export function puedeDevolverSena(orden: OrdenTaller): boolean {
  return orden.estado === "ANULADO" && saldoDeLaOrden(orden).cobrado > 0;
}

/** Solo `PRESUPUESTADO` exige datos extra: sin importe la base lo rechaza. */
export function pideImporte(estado: EstadoOt): boolean {
  return estado === "PRESUPUESTADO";
}

// ─────────────────────────────────────────────────────────────────────────────
// El presupuesto
// ─────────────────────────────────────────────────────────────────────────────

export interface EntradaPresupuesto {
  /** `null` = vacio o no interpretable. NUNCA 0 por defecto. */
  importe: number | null;
  moneda: Moneda;
  /** Lo que se encontro. Opcional: pisa el diagnostico anterior si viene. */
  diagnostico?: string;
}

export interface PresupuestoValidado {
  ok: boolean;
  /** Que falta, dicho como se lo diria una persona. `null` si esta todo. */
  problema: string | null;
}

/**
 * Que le falta al presupuesto para poder guardarse.
 *
 * Espeja `cambiar_estado_ot()`: importe obligatorio y no negativo, moneda
 * siempre (la constraint `presupuesto_requiere_moneda` no acepta un importe sin
 * moneda). El cero se deja pasar porque la base lo deja pasar: un trabajo en
 * garantia se presupuesta en cero y se factura igual.
 */
export function validarPresupuesto(entrada: EntradaPresupuesto): PresupuestoValidado {
  if (entrada.importe === null || !Number.isFinite(entrada.importe)) {
    return {
      ok: false,
      problema: "Falta el importe. Un presupuesto sin número no es un presupuesto.",
    };
  }
  if (entrada.importe < 0) {
    return { ok: false, problema: "El presupuesto no puede ser negativo." };
  }
  if (entrada.moneda !== "ARS" && entrada.moneda !== "USD") {
    return { ok: false, problema: "Falta decir en qué moneda está el presupuesto." };
  }
  return { ok: true, problema: null };
}

/** El `p_datos` de `cambiar_estado_ot()` para una transicion cualquiera. */
export function datosDelCambio(
  estado: EstadoOt,
  entrada: { presupuesto?: EntradaPresupuesto; observaciones?: string },
): Record<string, unknown> {
  const datos: Record<string, unknown> = {};

  if (pideImporte(estado) && entrada.presupuesto) {
    const { importe, moneda, diagnostico } = entrada.presupuesto;
    if (importe !== null) datos.presupuesto_importe = importe;
    datos.presupuesto_moneda = moneda;
    if (diagnostico && diagnostico.trim()) datos.diagnostico = diagnostico.trim();
  }

  // La base CONCATENA las observaciones a las que ya habia, no las pisa. Por eso
  // el campo va vacio en cada cambio: lo que se escribe es una nota nueva.
  if (entrada.observaciones && entrada.observaciones.trim()) {
    datos.observaciones = entrada.observaciones.trim();
  }

  return datos;
}

// ─────────────────────────────────────────────────────────────────────────────
// Dias en el taller
// ─────────────────────────────────────────────────────────────────────────────

const MS_POR_DIA = 24 * 60 * 60 * 1000;

/** Fecha a medianoche local: para contar dias, la hora es ruido. */
function aDia(valor: string | Date): Date | null {
  const d = typeof valor === "string" ? new Date(valor) : valor;
  if (Number.isNaN(d.getTime())) return null;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/**
 * Cuantos dias hace que el equipo esta en el local.
 *
 * Si la vista ya lo trajo, gana ese numero: lo calculo Postgres con
 * `current_date` y es el mismo que usa `v_ot_sin_retirar` para decidir la
 * alerta. Recalcularlo en el navegador solo agrega una chance de que la lista y
 * la alerta muestren dias distintos por una diferencia de huso horario.
 */
export function diasEnTaller(orden: OrdenTaller, hoy: Date = new Date()): number {
  if (orden.dias_en_taller !== null && orden.dias_en_taller !== undefined) {
    return Math.max(0, Math.round(aNumero(orden.dias_en_taller)));
  }
  const ingreso = aDia(orden.fecha_ingreso);
  const dia = aDia(hoy);
  if (!ingreso || !dia) return 0;
  return Math.max(0, Math.round((dia.getTime() - ingreso.getTime()) / MS_POR_DIA));
}

/** Dias en taller como texto corto: "hoy", "1 día", "12 días". */
export function textoDias(dias: number): string {
  if (dias <= 0) return "hoy";
  return dias === 1 ? "1 día" : `${dias} días`;
}

/**
 * Cuanto hace, o cuanto estuvo, como frase: "entró hoy", "hace 3 días",
 * "estuvo 3 días", "se fue el mismo día".
 *
 * Los encabezados armaban «hace {textoDias}», que con cero daba «hace hoy», y
 * en una orden cerrada «hace 22 días» ya no dice cuanto hace de nada: son los
 * dias que estuvo en el local.
 */
export function textoAntiguedad(orden: { estado: string }, dias: number): string {
  const cerrada = orden.estado === "ENTREGADO" || orden.estado === "ANULADO";
  if (cerrada) return dias <= 0 ? "se fue el mismo día" : `estuvo ${textoDias(dias)}`;
  return dias <= 0 ? "entró hoy" : `hace ${textoDias(dias)}`;
}

/**
 * Los dias para la columna de la lista, cortos: "hoy", "3 días"; y en las
 * cerradas "mismo día", "estuvo 3 días".
 *
 * Desde que los dias de una cerrada paran al entregar o anular (0131), la
 * columna mostraba «hoy» en equipos que se habian ido hacia semanas el mismo
 * dia que entraron: se leia como «fue hoy».
 */
export function textoDiasDeLista(orden: { estado: string }, dias: number): string {
  const cerrada = orden.estado === "ENTREGADO" || orden.estado === "ANULADO";
  if (cerrada) return dias <= 0 ? "mismo día" : `estuvo ${textoDias(dias)}`;
  return textoDias(dias);
}

// ─────────────────────────────────────────────────────────────────────────────
// Equipos sin retirar
// ─────────────────────────────────────────────────────────────────────────────

/** El umbral de `v_ot_sin_retirar`: mas de 7 dias en el local. */
export const DIAS_SIN_RETIRAR = 7;

/** Los dos estados en los que el equipo esta terminado y sigue en el mostrador. */
export const ESTADOS_SIN_RETIRAR: readonly EstadoOt[] = ["LISTO", "SIN_ARREGLO"];

/**
 * Los equipos que nadie vino a buscar.
 *
 * Misma regla que `v_ot_sin_retirar`: terminados (LISTO o SIN_ARREGLO) y con
 * mas de siete dias en el taller. `SIN_ARREGLO` cuenta igual que `LISTO` —el
 * equipo que no se pudo arreglar ocupa el mismo estante y es la misma
 * responsabilidad que el que si.
 *
 * Van ordenados del mas viejo al mas nuevo: el que hace tres meses que esta es
 * el que hay que llamar primero.
 */
export function sinRetirar(
  ordenes: ReadonlyArray<OrdenTaller>,
  hoy: Date = new Date(),
  umbral: number = DIAS_SIN_RETIRAR,
): OrdenTaller[] {
  return ordenes
    .filter((o) => ESTADOS_SIN_RETIRAR.includes(o.estado) && diasEnTaller(o, hoy) > umbral)
    .sort((a, b) => diasEnTaller(b, hoy) - diasEnTaller(a, hoy));
}

// ─────────────────────────────────────────────────────────────────────────────
// Facturacion
// ─────────────────────────────────────────────────────────────────────────────

/** Los estados desde los que `facturar_orden_reparacion()` acepta trabajar. */
export const ESTADOS_FACTURABLES: readonly EstadoOt[] = ["LISTO", "ENTREGADO", "SIN_ARREGLO"];

/** Una orden con comprobante ya paso por caja. La base no deja facturarla de nuevo. */
export function estaFacturada(orden: OrdenTaller): boolean {
  return Boolean(orden.comprobante);
}

/**
 * Que impide facturar esta orden. `null` = se puede.
 *
 * Los tres motivos son los tres `raise exception` de
 * `facturar_orden_reparacion()`, dichos ANTES del clic en vez de despues.
 */
export function motivoNoFacturable(orden: OrdenTaller): string | null {
  if (estaFacturada(orden)) {
    return `Ya se facturó con el comprobante ${orden.comprobante}. Una orden no se factura dos veces.`;
  }
  if (!ESTADOS_FACTURABLES.includes(orden.estado)) {
    return `La orden está en ${ETIQUETA_ESTADO[orden.estado].toLowerCase()}; se factura cuando el trabajo terminó.`;
  }
  if (aNumero(orden.mano_obra_importe) <= 0 && aNumero(orden.repuestos) <= 0) {
    return "No tiene mano de obra ni repuestos cargados: no hay nada para facturar.";
  }
  return null;
}

export function puedeFacturar(orden: OrdenTaller): boolean {
  return motivoNoFacturable(orden) === null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Repuestos
// ─────────────────────────────────────────────────────────────────────────────

export interface ResumenRepuestos {
  cantidad: number;
  costoArs: number;
  precioArs: number;
  /** Precio menos costo, en pesos. Lo que deja la parte de repuestos. */
  margenArs: number;
  /**
   * Si los repuestos ya salieron del stock. Se deduce del estado: el descuento
   * ocurre al pasar a EN_REPARACION y se repone al ANULAR.
   */
  descontados: boolean;
}

/**
 * Los repuestos de la orden, en numeros.
 *
 * `v_ordenes_reparacion` trae los totales, no las lineas: `orden_repuestos` no
 * tiene endpoint propio. Por eso la ficha muestra cuantos son y cuanto suman, y
 * no un detalle que el backend todavia no expone.
 */
export function resumirRepuestos(orden: OrdenTaller): ResumenRepuestos {
  const costoArs = aNumero(orden.costo_repuestos_ars);
  const precioArs = aNumero(orden.precio_repuestos_ars);
  return {
    cantidad: Math.round(aNumero(orden.repuestos)),
    costoArs,
    precioArs,
    margenArs: precioArs - costoArs,
    descontados: repuestosDescontados(orden.estado),
  };
}

/**
 * Si en este estado los repuestos ya salieron del stock.
 *
 * El descuento pasa al entrar a EN_REPARACION, asi que de ahi en adelante estan
 * afuera; ANULADO los repone. Antes de EN_REPARACION siguen en el deposito y se
 * le pueden vender a otro: mientras el cliente no aprueba, el repuesto no esta
 * reservado. Decirlo es la unica forma de que nadie lo cuente dos veces.
 */
export function repuestosDescontados(estado: EstadoOt): boolean {
  return ["EN_REPARACION", "LISTO", "ENTREGADO", "SIN_ARREGLO"].includes(estado);
}

// ─────────────────────────────────────────────────────────────────────────────
// El tablero
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Las columnas del tablero, en el orden del flujo.
 *
 * `v_taller_tablero` excluye ENTREGADO y ANULADO —el tablero es lo que HAY en
 * el taller, no el historico— y por eso tampoco aparecen aca.
 */
export const ESTADOS_TABLERO: readonly EstadoOt[] = [
  "RECIBIDO",
  "DIAGNOSTICO",
  "PRESUPUESTADO",
  "APROBADO",
  "EN_REPARACION",
  "LISTO",
  "SIN_ARREGLO",
];

export interface FilaTablero {
  estado: EstadoOt;
  ordenes: number;
  diasPromedio: number;
  diasMax: number;
}

/**
 * El tablero completo, con los estados vacios incluidos.
 *
 * La vista solo devuelve los estados que tienen algo. Mostrar unicamente esos
 * hace que el tablero cambie de forma cada vez que entra o sale un equipo, y
 * que "no hay nada en diagnostico" —que es informacion— se vea igual que "esa
 * columna no existe".
 */
export function leerTablero(tablero: TableroTaller["tablero"] | undefined): FilaTablero[] {
  const porEstado = new Map<string, Record<string, unknown>>();
  for (const fila of tablero ?? []) porEstado.set(String(fila.estado), fila);

  return ESTADOS_TABLERO.map((estado) => {
    const fila = porEstado.get(estado);
    return {
      estado,
      ordenes: Math.round(aNumero(fila?.ordenes as Numerico)),
      diasPromedio: aNumero(fila?.dias_promedio as Numerico),
      diasMax: Math.round(aNumero(fila?.dias_max as Numerico)),
    };
  });
}

/** Cuantos equipos hay adentro del local, sumando todas las columnas. */
export function totalEnTaller(filas: ReadonlyArray<FilaTablero>): number {
  return filas.reduce((acc, f) => acc + f.ordenes, 0);
}

// ─────────────────────────────────────────────────────────────────────────────
// El buscador
// ─────────────────────────────────────────────────────────────────────────────

export function normalizar(texto: string): string {
  return texto
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

const SOLO_DIGITOS = /\D/g;

/**
 * Busqueda de una orden.
 *
 * Las tres formas en que alguien pregunta por un equipo son el apellido
 * ("¿esta el de Martinez?"), el numero de orden que dice el papelito y el IMEI
 * cuando no hay ninguna de las dos. Tambien se busca por equipo y modelo,
 * porque a veces lo unico que se sabe es "el 13 Pro negro".
 *
 * El IMEI se compara ademas sin separadores: quien lo lee de una etiqueta lo
 * tipea con espacios o guiones y eso no puede hacer que no aparezca.
 *
 * Se busca palabra por palabra, en cualquier orden: «Rovere Fabian» encuentra a
 * «Fabian Rovere», y «rovere 13» al 13 de Rovere. Cada palabra tiene que
 * aparecer en algún dato de la orden. Y el número se entiende como lo dice el
 * papelito: «OT-4», «ot4» u «OT 0004» encuentran la OT-000004 (y no la 14).
 */
export function coincide(orden: OrdenTaller, consulta: string): boolean {
  const q = normalizar(consulta);
  if (!q) return true;

  const campos = [orden.cliente, orden.numero, orden.imei, orden.equipo, orden.modelo]
    .filter((c): c is string => Boolean(c))
    .map(normalizar);
  if (campos.some((c) => c.includes(q))) return true;

  // El IMEI entero, con o sin separadores.
  const digitos = q.replace(SOLO_DIGITOS, "");
  const imei = orden.imei ? orden.imei.replace(SOLO_DIGITOS, "") : "";
  if (digitos.length >= 3 && imei.includes(digitos) && /^[\d\s.\-/]+$/.test(q)) return true;

  // El número de la orden escrito corto: «OT-4» es la OT-000004.
  const numeroOrden = Number((orden.numero ?? "").replace(SOLO_DIGITOS, ""));
  const corto = /^ot\s*-?\s*0*(\d+)$/.exec(q);
  if (corto) return Number(corto[1]) === numeroOrden;

  const palabras = q
    .split(/\s+/)
    .map((p) => p.replace(/[,;]+$/, ""))
    .filter(Boolean);
  if (palabras.length < 2) return false;
  return palabras.every((p) => {
    const otCorto = /^ot-?0*(\d+)$/.exec(p);
    if (otCorto) return Number(otCorto[1]) === numeroOrden;
    return campos.some((c) => c.includes(p));
  });
}

export function buscarOrdenes(
  ordenes: ReadonlyArray<OrdenTaller>,
  consulta: string,
): OrdenTaller[] {
  const q = consulta.trim();
  if (!q) return [...ordenes];
  return ordenes.filter((o) => coincide(o, q));
}

/**
 * Filtra por estado. `null` = todos.
 *
 * El filtro es del lado del navegador y no del endpoint a proposito: entran de
 * dos a cinco equipos por dia, asi que las ultimas doscientas ordenes son meses
 * de taller. Traerlas una vez y filtrar en memoria hace que tocar una columna
 * del tablero o tipear en el buscador conteste al instante, sin un viaje al
 * servidor por cada tecla.
 */
export function filtrarPorEstado(
  ordenes: ReadonlyArray<OrdenTaller>,
  estado: EstadoOt | null,
): OrdenTaller[] {
  if (!estado) return [...ordenes];
  return ordenes.filter((o) => o.estado === estado);
}

// ─────────────────────────────────────────────────────────────────────────────
// Recibir un equipo
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Lo que se tipea en el formulario de recepcion: los campos del talon de papel
 * mas el IMEI, que distingue dos equipos iguales y permite buscar la orden
 * cuando el equipo vuelve con garantia. Modelo, color y accesorios van en
 * texto libre dentro de "equipo" u "observaciones", como en el papel; la plata
 * y la garantia no se preguntan al recibir.
 */
export interface FormularioRecepcion {
  equipo: string;
  imei: string;
  falla: string;
  clienteId: number | null;
  /**
   * Orden interna: un usado del stock que se manda a reacondicionar. Sin
   * cliente, sin diagnostico que cobrar; lo que salga se suma al costo del
   * equipo. `productoId` es el usado elegido, y es obligatorio si `esInterna`.
   */
  esInterna: boolean;
  productoId: number | null;
  /** Apple ID o cuenta de Google del equipo. */
  cuenta: string;
  password: string;
  /**
   * Como se abre el equipo: con un numero o con un patron. Es lo que el talon
   * llama "clave o patron". Se guarda el del tipo elegido; el otro no viaja.
   */
  tipoClave: "pin" | "patron";
  pin: string;
  /**
   * El patron de desbloqueo como la secuencia de puntos tocados en la grilla
   * 3x3, numerados del 1 al 9. Vacio es "no tiene patron".
   */
  patron: number[];
  transporte: string;
  tercerizado: string;
  observaciones: string;

  /**
   * El cobro del diagnostico, que es lo que habilita la mesa de trabajo.
   *
   * `cobra` arranca prendido: es la regla del negocio. Apagarlo exige decir por
   * que, y no impide dar de alta el equipo. Va a llegar el conocido un viernes
   * a las ocho; si el sistema no dejara cargarlo, el equipo quedaria en el
   * taller sin orden, que es peor que no cobrar.
   */
  cobraDiagnostico: boolean;
  diagnosticoImporte: number | null;
  diagnosticoMoneda: Moneda;
  diagnosticoMotivo: string;
  /** La caja donde entra la plata. Null = todavia no se eligio. */
  diagnosticoCuentaId: number | null;
  diagnosticoMedio: string;
  /** Lo que entra a la caja, en la moneda de la caja. */
  diagnosticoCobrado: number | null;
}

/**
 * La garantia no se pregunta al recibir: es siempre la misma y se aplica sola.
 * La fecha de vencimiento la calcula la base el dia que se entrega el equipo.
 */
export const GARANTIA_POR_DEFECTO = 90;

/**
 * El formulario limpio. `precarga` trae transporte y tercerizado de la ultima
 * orden: el taller manda casi siempre al mismo tecnico por la misma
 * encomienda, y tipearlo cada vez es lo que hace que se deje vacio.
 */
export function formularioVacio(precarga?: {
  transporte?: string | null;
  tercerizado?: string | null;
  /** Lo que cobra el local por el diagnostico, de la configuracion. */
  diagnostico?: number | null;
}): FormularioRecepcion {
  return {
    equipo: "",
    imei: "",
    falla: "",
    clienteId: null,
    esInterna: false,
    productoId: null,
    cuenta: "",
    password: "",
    tipoClave: "pin",
    pin: "",
    patron: [],
    transporte: precarga?.transporte ?? "",
    tercerizado: precarga?.tercerizado ?? "",
    observaciones: "",
    cobraDiagnostico: true,
    diagnosticoImporte: precarga?.diagnostico ?? null,
    diagnosticoMoneda: "USD",
    diagnosticoMotivo: "",
    diagnosticoCuentaId: null,
    diagnosticoMedio: "EFECTIVO_ARS",
    diagnosticoCobrado: null,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// El patron de desbloqueo
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Android no acepta un patron de menos de cuatro puntos, y la grilla tiene
 * nueve. El `check` de la columna (`0031_talon_reparacion.sql`) dice lo mismo.
 */
export const PATRON_MINIMO = 4;
export const PATRON_MAXIMO = 9;

/** "1-2-3-6-9": como lo guarda la base y como se lee en el talon. */
export function patronATexto(puntos: readonly number[]): string {
  return puntos.join("-");
}

/**
 * Lo inverso, para mostrar un patron guardado. Devuelve `[]` si el texto no es
 * un patron: mejor una grilla vacia que una grilla con puntos inventados.
 */
export function textoAPatron(texto: string | null | undefined): number[] {
  if (!texto || !/^[1-9](-[1-9]){3,8}$/.test(texto)) return [];
  return texto.split("-").map(Number);
}

/**
 * Que pasa si se toca el punto `punto` con la secuencia `actual`:
 *   - un punto nuevo se agrega al final;
 *   - tocar el ULTIMO punto lo saca (deshacer);
 *   - tocar un punto ya usado en el medio no hace nada, un patron no pasa dos
 *     veces por el mismo lugar.
 */
export function tocarPunto(actual: readonly number[], punto: number): number[] {
  if (punto < 1 || punto > PATRON_MAXIMO || !Number.isInteger(punto)) return [...actual];
  const i = actual.indexOf(punto);
  if (i === -1) return actual.length >= PATRON_MAXIMO ? [...actual] : [...actual, punto];
  if (i === actual.length - 1) return actual.slice(0, -1);
  return [...actual];
}

/**
 * El punto que queda EN EL MEDIO cuando se va derecho de `a` a `b` saltando
 * uno: del 1 al 3 se pasa por el 2, del 1 al 9 por el 5, del 2 al 8 por el 5.
 * `null` si son vecinos o si el salto no pasa por ningun punto (del 1 al 6, el
 * salto de caballo).
 */
export function puntoDelMedio(a: number, b: number): number | null {
  const fa = Math.floor((a - 1) / 3);
  const ca = (a - 1) % 3;
  const fb = Math.floor((b - 1) / 3);
  const cb = (b - 1) % 3;
  const df = fb - fa;
  const dc = cb - ca;
  if (df % 2 !== 0 || dc % 2 !== 0 || (df === 0 && dc === 0)) return null;
  return (fa + df / 2) * 3 + (ca + dc / 2) + 1;
}

/**
 * Que pasa cuando el dedo, arrastrando, llega al punto `punto`.
 *
 * Es la regla del telefono: un punto ya usado se ignora (no hay "deshacer"
 * arrastrando, el dedo pasa por arriba todo el tiempo), y si el trazo salta
 * por encima de un punto libre, ese punto entra tambien. Del 1 al 3 arrastrando
 * se marca el 2, porque la linea pasa por ahi y asi lo lee cualquier Android.
 */
export function arrastrarHasta(actual: readonly number[], punto: number): number[] {
  if (punto < 1 || punto > PATRON_MAXIMO || !Number.isInteger(punto)) return [...actual];
  if (actual.includes(punto)) return [...actual];
  const salida = [...actual];
  const ultimo = salida[salida.length - 1];
  if (ultimo !== undefined) {
    const medio = puntoDelMedio(ultimo, punto);
    if (medio !== null && !salida.includes(medio)) salida.push(medio);
  }
  salida.push(punto);
  return salida.slice(0, PATRON_MAXIMO);
}

export interface RecepcionValidada {
  ok: boolean;
  /** Un mensaje por campo, para poner debajo del campo y no en un toast. */
  problemas: Partial<Record<"equipo" | "falla" | "producto" | "patron" | "diagnostico", string>>;
}

// ─────────────────────────────────────────────────────────────────────────────
// Los pasos del formulario
// ─────────────────────────────────────────────────────────────────────────────

/**
 * El talon entero de una vez son doce campos en una pantalla, y eso asusta.
 * De a cuatro pasos es lo mismo que hacer el papel de arriba a abajo: primero
 * el equipo, despues de quien es, despues como se abre, despues adonde va.
 *
 * Solo el primer paso tiene campos obligatorios. Desde cualquiera se puede
 * guardar ya: los pasos siguientes son opcionales y se completan despues.
 */
export const PASOS_RECEPCION = [
  { clave: "equipo", titulo: "El equipo" },
  { clave: "titular", titulo: "De quién es" },
  { clave: "accesos", titulo: "Cómo se abre" },
  { clave: "envio", titulo: "Adónde va" },
  { clave: "diagnostico", titulo: "El diagnóstico" },
] as const;

export type PasoRecepcion = (typeof PASOS_RECEPCION)[number]["clave"];

type CampoConProblema = keyof RecepcionValidada["problemas"];

const CAMPOS_DEL_PASO: Record<PasoRecepcion, ReadonlyArray<CampoConProblema>> = {
  equipo: ["equipo", "falla", "producto"],
  titular: [],
  accesos: ["patron"],
  envio: [],
  diagnostico: ["diagnostico"],
};

/**
 * Los pasos que se muestran. Una orden interna no tiene titular ni diagnostico
 * que cobrar: esos dos pasos no aparecen, en vez de aparecer vacios.
 */
export function pasosDeRecepcion(
  f: FormularioRecepcion,
): ReadonlyArray<(typeof PASOS_RECEPCION)[number]> {
  if (!f.esInterna) return PASOS_RECEPCION;
  return PASOS_RECEPCION.filter((p) => p.clave !== "titular" && p.clave !== "diagnostico");
}

/** De todos los problemas del formulario, los que se muestran en `pasos`. */
export function problemasDeLosPasos(
  problemas: RecepcionValidada["problemas"],
  pasos: ReadonlySet<PasoRecepcion>,
): RecepcionValidada["problemas"] {
  const visibles: RecepcionValidada["problemas"] = {};
  for (const paso of pasos) {
    for (const campo of CAMPOS_DEL_PASO[paso]) {
      if (problemas[campo]) visibles[campo] = problemas[campo];
    }
  }
  return visibles;
}

/** Si el paso `paso` esta bien como para seguir al siguiente. */
export function puedeAvanzar(f: FormularioRecepcion, paso: PasoRecepcion): boolean {
  const { problemas } = validarRecepcion(f);
  return CAMPOS_DEL_PASO[paso].every((campo) => !problemas[campo]);
}

/**
 * Que falta para poder recibir el equipo.
 *
 * Dos campos y nada mas, los mismos dos que exige la base (`equipo not null`,
 * `falla_declarada not null`). El cliente NO es obligatorio: entra alguien
 * apurado, deja el equipo, y pedirle la ficha completa en ese momento es
 * exactamente la friccion que hace que la orden nunca se cargue. Una orden con
 * el equipo y la falla ya sirve; una orden que no existe, no.
 */
export function validarRecepcion(f: FormularioRecepcion): RecepcionValidada {
  const problemas: RecepcionValidada["problemas"] = {};

  if (!f.equipo.trim()) problemas.equipo = "Decí qué equipo es. Alcanza con «iPhone 13».";
  if (!f.falla.trim()) problemas.falla = "Anotá qué dice el cliente que le pasa.";

  if (f.esInterna && f.productoId === null) {
    problemas.producto = "Elegí qué usado del stock se manda.";
  }

  // Un patron a medio dibujar no se guarda: la base lo rechaza y el tecnico no
  // podria desbloquear el equipo con el.
  if (f.tipoClave === "patron" && f.patron.length > 0 && f.patron.length < PATRON_MINIMO) {
    problemas.patron = `El patrón necesita al menos ${PATRON_MINIMO} puntos. Tocá más puntos o borralo.`;
  }

  // No cobrar el diagnostico esta permitido; no decir por que, no. Es la unica
  // forma de que dentro de un mes se sepa si fue una excepcion o un olvido.
  // Una interna no tiene diagnostico que cobrar.
  if (!f.esInterna) {
    if (!f.cobraDiagnostico && !f.diagnosticoMotivo.trim()) {
      problemas.diagnostico = "Si no se cobra, escribí por qué. Queda anotado en la orden.";
    }
    if (f.cobraDiagnostico && (f.diagnosticoImporte ?? 0) > 0 && f.diagnosticoCuentaId === null) {
      problemas.diagnostico = "Elegí en qué caja entra la plata.";
    }
  }

  return { ok: Object.keys(problemas).length === 0, problemas };
}

/**
 * El cuerpo del POST, sin los campos vacios.
 *
 * Mandar `""` en vez de omitir el campo guarda cadenas vacias en la base, que
 * despues se ven como un IMEI que existe y esta en blanco.
 */
export function armarRecepcion(f: FormularioRecepcion): NuevaOrdenReparacion {
  const texto = (v: string): string | undefined => (v.trim() ? v.trim() : undefined);

  const comunes = {
    equipo: f.equipo.trim(),
    falla_declarada: f.falla.trim(),
    ...(texto(f.imei) ? { imei: texto(f.imei) } : {}),
    ...(texto(f.cuenta) ? { cuenta_equipo: texto(f.cuenta) } : {}),
    ...(texto(f.password) ? { password_equipo: texto(f.password) } : {}),
    // Clave o patron: viaja el del tipo elegido. Si se tipeo un numero y
    // despues se dibujo un patron, vale el patron, y al reves.
    ...(f.tipoClave === "pin" && texto(f.pin) ? { pin_equipo: texto(f.pin) } : {}),
    ...(f.tipoClave === "patron" && f.patron.length >= PATRON_MINIMO
      ? { patron_equipo: patronATexto(f.patron) }
      : {}),
    ...(texto(f.transporte) ? { transporte: texto(f.transporte) } : {}),
    ...(texto(f.tercerizado) ? { tercerizado: texto(f.tercerizado) } : {}),
    garantia_dias: GARANTIA_POR_DEFECTO,
    ...(texto(f.observaciones) ? { observaciones: texto(f.observaciones) } : {}),
  };

  // Interna: el usado en vez del cliente, y sin diagnostico. El cliente que
  // pudiera haber quedado elegido antes de marcar la casilla no viaja.
  if (f.esInterna) {
    return { ...comunes, producto_id: f.productoId ?? 0 };
  }

  return {
    ...comunes,
    ...(f.clienteId !== null ? { cliente_id: f.clienteId } : {}),
    diagnostico: armarDiagnostico(f),
  };
}

/**
 * El cobro del diagnostico que viaja con el alta.
 *
 * Con `cobraDiagnostico` apagado viaja el motivo y ninguna caja: la base
 * registra cuanto se le debe y por que no se cobro. Con el prendido viaja la
 * caja y, si el usuario piso el numero, tambien lo que entro de verdad.
 */
export function armarDiagnostico(f: FormularioRecepcion): CobroDiagnostico {
  const importe = f.diagnosticoImporte ?? 0;
  if (!f.cobraDiagnostico) {
    return {
      importe,
      moneda: f.diagnosticoMoneda,
      motivo: f.diagnosticoMotivo.trim() || "No se cobró al recibir",
    };
  }
  return {
    importe,
    moneda: f.diagnosticoMoneda,
    ...(f.diagnosticoCuentaId !== null ? { cuenta_id: f.diagnosticoCuentaId } : {}),
    medio: f.diagnosticoMedio as MedioPago,
    ...(f.diagnosticoCobrado !== null && f.diagnosticoCobrado > 0
      ? { importe_cobrado: f.diagnosticoCobrado }
      : {}),
  };
}

/**
 * Elegir el usado precarga lo que la ficha ya sabe: nombre e IMEI. Con `null`
 * se deselecciona y queda lo que se haya tipeado.
 */
export function precargarUsado(f: FormularioRecepcion, p: Producto | null): FormularioRecepcion {
  if (!p) return { ...f, productoId: null };
  return { ...f, esInterna: true, productoId: p.id, equipo: p.nombre, imei: p.imei ?? "" };
}

/**
 * Los usados que se pueden mandar a reacondicionar: los del local, con stock.
 * Un «a pedido» es del proveedor y uno vendido ya no esta. Se busca por nombre
 * o por IMEI, y el IMEI se compara sin espacios, como lo lee el buscador de
 * ordenes.
 */
export function usadosElegibles(
  usados: ReadonlyArray<Producto>,
  texto: string,
  tope = 8,
): Producto[] {
  const q = normalizar(texto);
  const digitos = q.replace(SOLO_DIGITOS, "");
  return usados
    .filter((p) => p.es_usado && !p.a_pedido && aNumero(p.stock_actual) > 0)
    .filter((p) => {
      if (!q) return true;
      if (normalizar(p.nombre).includes(q)) return true;
      return digitos.length > 0 && (p.imei ?? "").replace(SOLO_DIGITOS, "").includes(digitos);
    })
    .slice(0, tope);
}

/** El mapa producto → orden abierta, que es como lo consultan la lista y el mostrador. */
export function indexarEnTaller(filas: ReadonlyArray<UsadoEnTaller>): Map<number, UsadoEnTaller> {
  return new Map(filas.map((f) => [f.producto_id, f]));
}

// ─────────────────────────────────────────────────────────────────────────────
// Textos que se repiten
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Lo que devuelve `POST /reparaciones/:id/facturar`.
 *
 * `useFacturarOt()` lo tipa como `ResultadoVenta`, pero el RPC no devuelve la
 * venta pelada: devuelve `{ orden_id, numero, venta }`, donde `numero` es el de
 * la ORDEN y la venta va adentro. Se lee aca, con cuidado, en vez de escribir un
 * `as ResultadoVenta` en la pantalla que despues muestre "undefined" en el
 * toast.
 */
export interface FacturacionHecha {
  orden: string | null;
  comprobante: string | null;
  total: number | null;
  moneda: Moneda | null;
}

export function leerFacturacion(respuesta: unknown): FacturacionHecha {
  const r = (respuesta ?? {}) as Record<string, unknown>;
  // Si no vino `venta`, no se cae a `r`: el `numero` de la orden se leeria como
  // si fuera el del comprobante y el toast diria una mentira prolija.
  const venta =
    typeof r.venta === "object" && r.venta !== null ? (r.venta as Record<string, unknown>) : {};
  const total = venta.total as Numerico | undefined;

  return {
    orden: typeof r.numero === "string" ? r.numero : null,
    comprobante: typeof venta.numero === "string" ? venta.numero : null,
    total: total === undefined || total === null ? null : aNumero(total),
    moneda: venta.moneda === "USD" || venta.moneda === "ARS" ? venta.moneda : null,
  };
}

/**
 * La cotizacion como numero pelado, sin la unidad: "1.601".
 *
 * `formatearCotizacion()` de `@tora/shared` ya le pega el sufijo "ARS/USD", que
 * en una frase como "US$ 1 = $ 1.601" queda dicho dos veces. El formato (sin
 * decimales salvo que los tenga) es el de `formatearCotizacionNumero`.
 */
export function textoCotizacion(valor: number): string {
  return formatearCotizacionNumero(valor);
}

/** Como se nombra a la orden cuando hay que decirla en una frase. */
export function nombreOrden(orden: OrdenTaller): string {
  return orden.numero ?? `Orden ${orden.id}`;
}

/** Una orden interna apunta a un usado del stock en vez de a un cliente. */
export function esInterna(orden: OrdenTaller): boolean {
  return orden.producto_id !== null && orden.producto_id !== undefined;
}

/**
 * La etiqueta del estado de ESTA orden. En la interna no hay presupuesto que
 * aprobar: PRESUPUESTADO es que el tecnico ya dijo cuanto cobra, y el chip
 * hablaba de un presupuesto que nadie le paso a ningun cliente.
 */
export function etiquetaEstadoDe(orden: OrdenTaller): string {
  if (esInterna(orden) && orden.estado === "PRESUPUESTADO") return "Costo cargado";
  // Entregada no dice como volvio: una rechazada o sin arreglo se listaba
  // «Entregado» al lado del precio de una reparacion que no se hizo, y una
  // interna no se entrega a nadie, vuelve al stock.
  if (orden.estado === "ENTREGADO") {
    if (esInterna(orden)) return "Volvió al stock";
    if (orden.motivo_cierre === "RECHAZADO") return "Rechazado · devuelto";
    if (orden.motivo_cierre === "SIN_ARREGLO") return "Sin arreglo · devuelto";
  }
  return ETIQUETA_ESTADO[orden.estado];
}

/**
 * El diagnostico que quedo sin cobrar al recibir (con su motivo) y que todavia
 * no se cubrio, en su moneda. `null` si no hay nada pendiente o la orden ya se
 * cerro. La ficha decia «Todavía sin precio» y la lista «sin costos», y los
 * US$ 20 que el cliente tiene que pagar al retirar no figuraban en ningun lado.
 */
export function diagnosticoPendiente(
  orden: OrdenTaller,
): { falta: number; moneda: Moneda; motivo: string } | null {
  if (esInterna(orden) || !orden.diagnostico_motivo) return null;
  if (orden.estado === "ENTREGADO" || orden.estado === "ANULADO" || estaFacturada(orden)) {
    return null;
  }
  // Con precio y el equipo reparandose, el diagnostico va a cuenta de ese
  // precio: `entregar_orden` factura encomienda + tecnico + ganancia y nada
  // mas. La ficha lo mostraba debajo de «Precio al cliente US$ 120» como
  // «Diagnóstico pendiente US$ 20», y el cajero podia pedir US$ 140. Solo se
  // cobra aparte si el equipo vuelve sin arreglo o rechazado.
  const sinReparar = orden.estado === "SIN_ARREGLO" || orden.estado === "RECHAZADO";
  if (leerPlata(orden).precio !== null && !sinReparar) return null;
  const importe = aNumero(orden.diagnostico_importe);
  if (importe <= 0) return null;
  const moneda = orden.diagnostico_moneda ?? "USD";
  // Lo cobrado despues (una seña) lo cubre, igual que en `cobroAlVolver()`.
  const cobrado = aNumero(moneda === "USD" ? orden.senas_usd : orden.senas_ars);
  const falta = redondear2(importe - cobrado);
  return falta > 0.009 ? { falta, moneda, motivo: orden.diagnostico_motivo } : null;
}

/** Cuando se cerro la orden: la entrega o, si se anulo, el ultimo dia que conto. */
function fechaDeCierre(orden: OrdenTaller): number {
  if (orden.fecha_entrega) return new Date(orden.fecha_entrega).getTime();
  // La anulada no tiene fecha de entrega, y la vista no trae `anulado_at`: los
  // dias en taller dejan de contar ese dia (0131), asi que ingreso mas dias es
  // el dia en que se anulo.
  return new Date(orden.fecha_ingreso).getTime() + diasEnTaller(orden) * MS_POR_DIA;
}

/**
 * Los abiertos primero, el que mas espero arriba; los cerrados despues, el
 * ultimo que se cerro arriba.
 *
 * Los cerrados tambien se ordenaban por dias en el taller: lo entregado hoy en
 * el mismo dia quedaba al fondo, debajo de uno que estuvo un mes y se fue hace
 * semanas.
 */
export function ordenarParaTrabajar(ordenes: ReadonlyArray<OrdenTaller>): OrdenTaller[] {
  return [...ordenes].sort((a, b) => {
    const cerradaA = esTerminal(a.estado);
    const cerradaB = esTerminal(b.estado);
    if (cerradaA !== cerradaB) return cerradaA ? 1 : -1;
    if (cerradaA) return fechaDeCierre(b) - fechaDeCierre(a) || b.id - a.id;
    return diasEnTaller(b) - diasEnTaller(a);
  });
}

/** Quien la dejo. La orden puede no tener cliente todavia: eso es valido. */
export function nombreCliente(orden: OrdenTaller): string {
  if (esInterna(orden)) return `Equipo propio · ${orden.producto ?? orden.equipo}`;
  return orden.cliente ?? "Sin cliente asociado";
}

// ─────────────────────────────────────────────────────────────────────────────
// El flujo real: recibir, presupuestar, entregar
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Las etapas que ve la pantalla. Los nueve estados siguen en la base y siguen
 * validados, pero el mostrador piensa en tres cosas: a que equipos les falta
 * el numero del tecnico, cuales ya se pueden entregar, y cuales ya se fueron.
 */
export type Etapa = "SIN_COSTOS" | "FALTA_PAGAR_TECNICO" | "PARA_ENTREGAR" | "CERRADAS";

/**
 * Tres reparten el circuito y una lo cruza: "falta pagarle al tecnico" es un
 * equipo al que todavia no se le asento el egreso, este donde este. No es un
 * estado del circuito, es un hecho sobre la plata, y por eso vive en
 * `pago_tecnico_at` y no en el enum. Estados para el circuito, fechas para los
 * hitos. Ver `enLaEtapa()`.
 */
export const ETAPAS: ReadonlyArray<{ clave: Etapa; titulo: string }> = [
  { clave: "SIN_COSTOS", titulo: "Falta el presupuesto" },
  { clave: "FALTA_PAGAR_TECNICO", titulo: "Falta pagar al técnico" },
  { clave: "PARA_ENTREGAR", titulo: "Para entregar" },
  // «Cerradas» y no «Entregadas»: la etapa junta las entregadas con las
  // anuladas, y el chip las contaba a todas con un nombre que solo decia una.
  { clave: "CERRADAS", titulo: "Cerradas" },
];

/** Las tres del circuito: cada orden cae en una, y en una sola. */
export type EtapaCircuito = Exclude<Etapa, "FALTA_PAGAR_TECNICO">;

/** Donde esta la orden en el circuito: falta el numero, se puede entregar, se fue. */
export function etapaDe(orden: OrdenTaller): EtapaCircuito {
  if (orden.estado === "ENTREGADO" || orden.estado === "ANULADO") return "CERRADAS";
  if (orden.estado === "RECIBIDO" || orden.estado === "DIAGNOSTICO") return "SIN_COSTOS";
  return "PARA_ENTREGAR";
}

/**
 * Si la orden entra en el chip.
 *
 * "Falta pagar al tecnico" NO es una etapa del circuito: es un hecho sobre la
 * plata, y por eso CRUZA el reparto en vez de ser una casilla mas. Una orden
 * entregada hace dos semanas con el tecnico sin pagar sigue debiendo plata, y
 * tiene que aparecer cuando se la busca sin dejar de ser una entregada.
 *
 * Antes se resolvia mandandola a "Entregadas" y listo, asi que el cartel
 * decia "le debes en 6 ordenes" y el chip mostraba una sola: las otras cinco
 * ya estaban entregadas y el filtro no las alcanzaba nunca. La deuda era real
 * (`pagar_tecnico_ot` deja saldar una entregada a proposito) pero no habia
 * forma de llegar a ella.
 *
 * Consecuencia asumida: los cuatro contadores ya no suman "Todas". Son
 * filtros, no una torta; que el cartel y el chip digan lo mismo vale mas que
 * que las cuentas cierren.
 */
export function enLaEtapa(orden: OrdenTaller, etapa: Etapa): boolean {
  if (etapa === "FALTA_PAGAR_TECNICO") return puedePagarTecnico(orden);
  return etapaDe(orden) === etapa;
}

export function filtrarPorEtapa(
  ordenes: ReadonlyArray<OrdenTaller>,
  etapa: Etapa | null,
): OrdenTaller[] {
  if (!etapa) return [...ordenes];
  return ordenes.filter((o) => enLaEtapa(o, etapa));
}

export function contarPorEtapa(ordenes: ReadonlyArray<OrdenTaller>): Record<Etapa, number> {
  const n: Record<Etapa, number> = {
    SIN_COSTOS: 0,
    FALTA_PAGAR_TECNICO: 0,
    PARA_ENTREGAR: 0,
    CERRADAS: 0,
  };
  for (const o of ordenes) {
    n[etapaDe(o)] += 1;
    if (puedePagarTecnico(o)) n.FALTA_PAGAR_TECNICO += 1;
  }
  return n;
}

/**
 * Si al tecnico ya se le pago. Sin costo de tecnico no hay nada que pagar, asi
 * que cuenta como saldado: una reparacion que hizo el local no le debe nada a
 * nadie.
 */
export function tecnicoPago(orden: OrdenTaller): boolean {
  // `Boolean(...)` y no `!== null`: la vista manda null y una orden recien
  // armada en memoria manda undefined. Distinguirlos aca daba por pagado lo
  // que nadie pago.
  return aNumero(orden.costo_tecnico_importe) <= 0 || Boolean(orden.pago_tecnico_at);
}

const redondear2 = (v: number): number => Math.round(v * 100) / 100;

/** Se le puede pagar mientras haya costo cargado, no se haya pagado, y la orden viva. */
export function puedePagarTecnico(orden: OrdenTaller): boolean {
  if (orden.estado === "ANULADO") return false;
  if (orden.estado === "SIN_ARREGLO" || orden.estado === "RECHAZADO") return false;
  // Entregar pasa el estado a ENTREGADO y el porque queda solo en
  // `motivo_cierre`. Mirando el estado nada mas, un equipo rechazado o sin
  // arreglo volvia a sumar «Le debés al técnico» apenas se lo llevaban, por
  // una reparacion que no se hizo.
  if (orden.motivo_cierre === "SIN_ARREGLO" || orden.motivo_cierre === "RECHAZADO") return false;
  return aNumero(orden.costo_tecnico_importe) > 0 && !orden.pago_tecnico_at;
}

// ── La deuda con el tecnico ──────────────────────────────────────────────────

export interface DeudaTecnico {
  /** Lo que falta pagar, por moneda de los costos. Solo las monedas con algo. */
  porMoneda: Partial<Record<Moneda, number>>;
  ordenes: number;
}

/**
 * Cuanto se le debe al tecnico: las ordenes con costo cargado y sin pago,
 * internas y de clientes por igual. Una entregada sin pagarle sigue contando,
 * porque entregar sin pagar no esta bloqueado a proposito.
 */
export function deudaTecnico(ordenes: ReadonlyArray<OrdenTaller>): DeudaTecnico {
  const porMoneda: Partial<Record<Moneda, number>> = {};
  let n = 0;
  for (const o of ordenes) {
    if (!puedePagarTecnico(o)) continue;
    const m = o.costos_moneda ?? "USD";
    porMoneda[m] = redondear2((porMoneda[m] ?? 0) + aNumero(o.costo_tecnico_importe));
    n += 1;
  }
  return { porMoneda, ordenes: n };
}

/** Un bloque de la liquidacion: todo lo que se debe en UNA moneda. */
export interface GrupoDeuda {
  moneda: Moneda;
  total: number;
  ordenes: OrdenTaller[];
}

/**
 * La deuda con el tecnico, partida por la moneda de los costos.
 *
 * No se convierte nada: si se deben dolares y pesos, son dos bloques y se
 * pagan por separado. Llevar todo a una sola moneda haria que el egreso en
 * pesos quedara asentado pasando por una conversion que nunca ocurrio.
 *
 * Dolares primero, que es como lo dice el cartel.
 */
export function deudaPorMoneda(ordenes: ReadonlyArray<OrdenTaller>): GrupoDeuda[] {
  const grupos: GrupoDeuda[] = [];
  for (const m of ["USD", "ARS"] as Moneda[]) {
    const suyas = ordenes
      .filter((o) => puedePagarTecnico(o) && (o.costos_moneda ?? "USD") === m)
      .sort((a, b) => a.id - b.id);
    if (suyas.length === 0) continue;
    grupos.push({
      moneda: m,
      total: redondear2(suyas.reduce((t, o) => t + aNumero(o.costo_tecnico_importe), 0)),
      ordenes: suyas,
    });
  }
  return grupos;
}

/** «Le debés al técnico US$ 120 y $ 45.000 en 3 órdenes». `null` sin deuda. */
export function textoDeudaTecnico(d: DeudaTecnico): string | null {
  if (d.ordenes === 0) return null;
  const partes = (["USD", "ARS"] as Moneda[])
    .filter((m) => (d.porMoneda[m] ?? 0) > 0)
    .map((m) => formatear(d.porMoneda[m] ?? 0, m));
  const cuantas = d.ordenes === 1 ? "1 orden" : `${d.ordenes} órdenes`;
  return `Le debés al técnico ${partes.join(" y ")} en ${cuantas}`;
}

/** Lo unico que se puede hacer ahora con esta orden, en grande. */
export type AccionPrincipal = "CARGAR_COSTOS" | "ENTREGAR" | "CERRADA";

export function accionPrincipal(orden: OrdenTaller): AccionPrincipal {
  if (esTerminal(orden.estado)) return "CERRADA";
  if (orden.estado === "SIN_ARREGLO") return "ENTREGAR";
  return orden.costos_at ? "ENTREGAR" : "CARGAR_COSTOS";
}

/** Una seña se puede dejar mientras la orden este abierta y sin facturar. */
export function puedeSena(orden: OrdenTaller): boolean {
  return !esInterna(orden) && !esTerminal(orden.estado) && !estaFacturada(orden);
}

// ── Los tres montos ──────────────────────────────────────────────────────────

export interface FormularioCostos {
  costoTecnico: number | null;
  ganancia: number | null;
  moneda: Moneda;
  diagnostico: string;
}

/**
 * Con que arranca el formulario: lo que ya tiene la orden si se esta
 * corrigiendo, o la regla del negocio (la ganancia precargada en dolares) si
 * es la primera vez. El costo del tecnico nunca se precarga: es lo unico que
 * hay que tipear con lo que contesto.
 */
export function costosIniciales(orden: OrdenTaller, regla: { ganancia: number }): FormularioCostos {
  if (orden.costos_at) {
    return {
      costoTecnico: aNumero(orden.costo_tecnico_importe),
      ganancia: aNumero(orden.ganancia_importe),
      moneda: orden.costos_moneda ?? "USD",
      diagnostico: orden.diagnostico ?? "",
    };
  }
  return {
    costoTecnico: null,
    ganancia: esInterna(orden) ? 0 : regla.ganancia,
    moneda: "USD",
    diagnostico: orden.diagnostico ?? "",
  };
}

/**
 * Cambiar la moneda de los costos.
 *
 * El selector solo cambiaba la etiqueta: la ganancia precargada (40 dolares)
 * quedaba como $ 40 al pasar a pesos, y el precio al cliente salia con 40
 * pesos de ganancia. Paso en una orden real. Ahora, si la ganancia sigue en el
 * valor de la regla, se pasa a la otra moneda al cambio del dia; si la persona
 * la escribio, se respeta: es un numero que alguien decidio.
 *
 * Sin cotizacion no hay como convertir, y la ganancia queda vacia para que se
 * escriba antes que dejar un numero en la moneda equivocada.
 */
export function cambiarMonedaCostos(
  f: FormularioCostos,
  moneda: Moneda,
  regla: { ganancia: number },
  cotizacion: number,
): FormularioCostos {
  if (f.moneda === moneda) return f;
  const reglaEn = (m: Moneda): number | null =>
    m === "USD" ? regla.ganancia : cotizacion > 0 ? redondear2(regla.ganancia * cotizacion) : null;
  const esLaDeLaRegla =
    regla.ganancia > 0 && f.ganancia !== null && f.ganancia === reglaEn(f.moneda);
  return {
    ...f,
    moneda,
    ganancia: esLaDeLaRegla ? reglaEn(moneda) : f.ganancia,
  };
}

/** El precio al cliente: tecnico mas ganancia. Lo vacio cuenta como cero. */
export function precioDeCostos(f: FormularioCostos): number {
  return redondear2((f.costoTecnico ?? 0) + (f.ganancia ?? 0));
}

/**
 * `interna`: en una orden de stock no hay ganancia (el campo no se muestra).
 *
 * La ganancia vacia de una orden de cliente frena, como el tecnico: queda vacia
 * cuando se pasa a pesos sin cotizacion (`cambiarMonedaCostos`), y antes el
 * presupuesto se guardaba igual con ganancia cero, sin aviso.
 */
export function validarCostos(
  f: FormularioCostos,
  interna = false,
): { ok: boolean; problema?: string } {
  if (f.costoTecnico === null) {
    return { ok: false, problema: "Falta lo que cobra el técnico. Si no cobra nada, poné cero." };
  }
  if (!interna && f.ganancia === null) {
    return { ok: false, problema: "Falta la ganancia. Si no se le gana nada, poné cero." };
  }
  for (const v of [f.costoTecnico, f.ganancia]) {
    if (v !== null && v < 0) return { ok: false, problema: "Los montos no pueden ser negativos." };
  }
  if (precioDeCostos(f) <= 0) {
    return { ok: false, problema: "Con los tres en cero no hay nada que cobrar." };
  }
  return { ok: true };
}

export function armarCostos(f: FormularioCostos): CostosOt {
  const diagnostico = f.diagnostico.trim();
  return {
    encomienda: 0,
    costo_tecnico: f.costoTecnico ?? 0,
    ganancia: f.ganancia ?? 0,
    moneda: f.moneda,
    ...(diagnostico ? { diagnostico } : {}),
  };
}

// ── La plata de la orden ─────────────────────────────────────────────────────

export interface PlataOrden {
  moneda: Moneda;
  /** `null` mientras el tecnico no contesto. */
  precio: number | null;
  encomienda: number;
  costoTecnico: number;
  ganancia: number;
  /** Las señas, en la moneda de los costos, al cambio del dia que entraron. */
  senas: number;
  /** Precio menos señas. `null` sin precio. */
  resta: number | null;
  facturado: boolean;
  pagado: number | null;
}

export function leerPlata(orden: OrdenTaller): PlataOrden {
  const moneda = orden.costos_moneda ?? "USD";
  const conCostos = Boolean(orden.costos_at);
  const encomienda = aNumero(orden.encomienda_importe);
  const costoTecnico = aNumero(orden.costo_tecnico_importe);
  const ganancia = aNumero(orden.ganancia_importe);
  const precio = conCostos ? redondear2(encomienda + costoTecnico + ganancia) : null;
  const senas = redondear2(aNumero(moneda === "USD" ? orden.senas_usd : orden.senas_ars));
  return {
    moneda,
    precio,
    encomienda,
    costoTecnico,
    ganancia,
    senas,
    resta: precio === null ? null : redondear2(Math.max(precio - senas, 0)),
    facturado: estaFacturada(orden),
    pagado:
      orden.pagado_facturado === null || orden.pagado_facturado === undefined
        ? null
        : aNumero(orden.pagado_facturado),
  };
}

/**
 * Lo que se le pagó de verdad al técnico, en la moneda de los costos, o `null`
 * si todavía no se le pagó.
 *
 * El costo del técnico que se carga con el presupuesto es lo COTIZADO (US$ 120).
 * Al pagarle se asienta lo que salió de la caja ($ 185.000), que al dólar del
 * técnico de ese día puede ser otra cosa (US$ 118,59). La orden y su historial
 * decían lo cotizado aunque la caja dijera otra cosa; desde el 05/11 dicen lo
 * pagado. Sale de `egresos_*` de la vista: el pago al técnico es el único
 * EGRESO con `orden_id` que cuenta ahí (las devoluciones van aparte), congelado
 * en las dos monedas al cambio del día en que salió.
 */
export function pagoAlTecnico(orden: OrdenTaller): number | null {
  if (!orden.pago_tecnico_at) return null;
  const moneda = orden.costos_moneda ?? "USD";
  const pagado = redondear2(aNumero(moneda === "USD" ? orden.egresos_usd : orden.egresos_ars));
  return pagado > 0 ? pagado : null;
}

export interface SaldoOrden {
  /** La moneda de los tres numeros de abajo. */
  moneda: Moneda;
  /** Lo cobrado por la orden (diagnostico, señas y, facturada, lo de la entrega). */
  cobrado: number;
  /** Lo que falta cobrar. `null` = no hay contra que comparar (sin precio, o anulada). */
  falta: number | null;
  /** Lo cobrado de mas: queda a favor del cliente. */
  aFavor: number;
  anulada: boolean;
  /** Lo que ya se le devolvio (solo de una anulada, `devolver_sena_ot`). */
  devuelto: number;
}

/**
 * Lo cobrado contra lo que corresponde cobrar, segun como termino la orden.
 *
 * `leerPlata().resta` es precio menos señas, y la ficha la mostraba como
 * «Falta cobrar» en cualquier estado: una orden sin arreglo, que solo factura
 * el diagnostico, decia «Falta cobrar US$ 48» al lado de «No hay nada que
 * cobrar», y una anulada pedia el precio entero. Y lo cobrado de mas (la seña
 * de una reparacion que no se hizo) no aparecia en ningun lado.
 *
 *   - Anulada: no se factura nada. Queda lo que se cobro, y nada que cobrar.
 *   - Vuelve sin reparar: se compara contra el diagnostico, en SU moneda (la
 *     misma cuenta que `evaluarEntrega()` y `cobroAlVolver()`).
 *   - Facturada: contra lo facturado, en la moneda del comprobante.
 *   - Si no: contra el precio.
 */
export function saldoDeLaOrden(orden: OrdenTaller): SaldoOrden {
  const plata = leerPlata(orden);
  const cobradoEn = (m: Moneda): number =>
    redondear2(aNumero(m === "USD" ? orden.senas_usd : orden.senas_ars));

  if (orden.estado === "ANULADO") {
    // Lo devuelto se resta: despues de «Devolver la seña» la ficha seguia
    // diciendo «Quedó cobrado» con la plata ya en manos del cliente. Cinco
    // centavos de tolerancia, como la base: devolver en la otra moneda deja
    // centavos de conversion que no son de nadie.
    const devuelto = redondear2(
      aNumero(plata.moneda === "USD" ? orden.devuelto_usd : orden.devuelto_ars),
    );
    const neto = redondear2(cobradoEn(plata.moneda) - devuelto);
    return {
      moneda: plata.moneda,
      cobrado: neto < 0.05 ? 0 : neto,
      falta: null,
      aFavor: 0,
      anulada: true,
      devuelto,
    };
  }

  let moneda: Moneda;
  let referencia: number | null;
  if (plata.facturado) {
    moneda = orden.moneda_facturado ?? plata.moneda;
    referencia = aNumero(orden.facturado);
  } else if (
    orden.motivo_cierre ||
    orden.estado === "SIN_ARREGLO" ||
    orden.estado === "RECHAZADO"
  ) {
    moneda = orden.diagnostico_moneda ?? plata.moneda;
    referencia = aNumero(orden.diagnostico_importe);
  } else {
    moneda = plata.moneda;
    referencia = plata.precio;
  }
  const cobrado = cobradoEn(moneda);
  if (referencia === null) {
    return { moneda, cobrado, falta: null, aFavor: 0, anulada: false, devuelto: 0 };
  }
  let diferencia = redondear2(referencia - cobrado);
  // Una factura en pesos cancelada con dolares no llega exacta: $ 93.600
  // cobrados con US$ 58,87 son $ 93.603,30, y la base la da por pagada con el
  // importe mas cercano (0121_8, 0121_9f). La ficha decia «A favor del
  // cliente $ 3,30». Hasta medio centavo de dolar, a la cotizacion a la que
  // entro la plata, no es de nadie.
  if (
    plata.facturado &&
    moneda === "ARS" &&
    plata.pagado !== null &&
    plata.pagado >= referencia - 0.009
  ) {
    const usd = aNumero(orden.senas_usd);
    const cotizacion = usd > 0 ? aNumero(orden.senas_ars) / usd : 0;
    if (Math.abs(diferencia) < 0.005 * cotizacion + 0.01) diferencia = 0;
  }
  return {
    moneda,
    cobrado,
    falta: Math.max(diferencia, 0),
    // Un centavo de diferencia sale de convertir cada pago a su cotizacion: no
    // es plata del cliente.
    aFavor: diferencia < -0.01 ? -diferencia : 0,
    anulada: false,
    devuelto: 0,
  };
}

/** La venta de la orden esta anulada: la reparacion quedo sin facturar. */
export function facturaAnulada(orden: OrdenTaller): boolean {
  return estaFacturada(orden) && orden.estado_comprobante === "ANULADO";
}

/**
 * Lo que la ficha tiene que marcar sobre la factura, o `null` si no hay nada.
 *
 * La ficha mostraba el precio y el comprobante uno abajo del otro sin
 * compararlos: una venta anulada seguia diciendo «Queda en cuenta» con lo que
 * se habia devuelto, una factura por otro importe pasaba sin marca, y una orden
 * entregada sin comprobante solo lo decia en el historial.
 */
export function avisoDeLaFactura(orden: OrdenTaller): string | null {
  if (esInterna(orden)) return null;
  const plata = leerPlata(orden);
  if (facturaAnulada(orden)) {
    return `La venta ${orden.comprobante} está anulada: la reparación quedó sin facturar.`;
  }
  if (estaFacturada(orden)) {
    // El equipo que vuelve sin repararse factura el diagnostico y nada mas: que
    // no coincida con el precio es lo esperado, pero hay que decirlo.
    if (orden.motivo_cierre) return "Volvió sin reparar: se facturó solo el diagnóstico.";
    const facturado = aNumero(orden.facturado);
    const moneda = orden.moneda_facturado ?? plata.moneda;
    if (
      plata.precio !== null &&
      moneda === plata.moneda &&
      Math.abs(facturado - plata.precio) > 0.009
    ) {
      return `Se facturó ${formatear(facturado, moneda)}, distinto del precio al cliente (${formatear(plata.precio, plata.moneda)}).`;
    }
    return null;
  }
  if (orden.estado === "ENTREGADO" && !orden.motivo_cierre && (plata.precio ?? 0) > 0) {
    return "Se entregó sin facturar: el precio no pasó por el cobro.";
  }
  return null;
}

/**
 * La orden con los costos que la base acaba de devolver.
 *
 * Al guardar el presupuesto, la pantalla quiere mostrarlo en el acto —el
 * mensaje de WhatsApp sale de ahi— pero la lista recien se esta invalidando y
 * la orden que hay en memoria todavia dice `costos_at: null`, o sea precio
 * cero. En vez de esperar al refetch (y mandar un presupuesto de $0 si tarda),
 * se pegan encima los numeros de la respuesta, que son los que la base guardo.
 *
 * El diagnostico se toma del formulario porque la respuesta no lo trae, y solo
 * si se escribio algo: guardar sin tocarlo no borra el que ya estaba.
 */
export function conCostosCargados(
  orden: OrdenTaller,
  r: ResultadoCostosOt,
  diagnostico?: string,
): OrdenTaller {
  return {
    ...orden,
    estado: r.estado,
    costos_at: new Date().toISOString(),
    costos_moneda: r.moneda,
    encomienda_importe: r.encomienda,
    costo_tecnico_importe: r.costo_tecnico,
    ganancia_importe: r.ganancia,
    precio_cliente: r.precio_cliente,
    diagnostico: diagnostico?.trim() ? diagnostico.trim() : orden.diagnostico,
  };
}

// ── Entregar ─────────────────────────────────────────────────────────────────

export interface PagoEntrega {
  id: string;
  cuenta_id: number;
  medio: string;
  /** `null` = vacío o no interpretable. Frena la entrega; nunca se vuelve 0 solo. */
  importe: number | null;
  /** Los datos del papel con CHEQUE o ECHEQ (0155): entra a cartera. */
  cheque?: { numero: string; banco: string; librador: string; fecha_cobro: string };
}

export interface FormularioEntrega {
  pagos: PagoEntrega[];
  /**
   * Por que vuelve el equipo. `null` = se repara y se entrega normal.
   *
   * Los dos motivos terminan igual para el cliente, pero se cuentan distinto:
   * RECHAZADO son las reparaciones que se pierden por precio.
   */
  cierre: MotivoCierreOt | null;
  /** El equipo que vuelve factura el diagnostico, que el cliente ya pago. */
  cobrarDiagnostico: boolean;
  /** Solo interna: el precio de venta nuevo del usado. Null = no se toca. */
  precioVenta: number | null;
  /**
   * Cuanto del saldo a favor del cliente paga esta entrega, en la moneda de la
   * entrega. Nunca se aplica solo: lo elige quien entrega, como en la venta
   * (0121_8). Sin esto el taller, que es el que genera esos saldos (la seña
   * de una reparacion rechazada), no tenia forma de usarlos.
   */
  saldoAFavor?: number;
  /** El cliente deja el vuelto de propina (0155). Solo vale si hay vuelto. */
  vueltoPropina?: boolean;
}

/** El equipo vuelve sin repararse, por el motivo que sea. */
export function vuelveSinReparar(orden: OrdenTaller, f: FormularioEntrega): boolean {
  return f.cierre !== null || orden.estado === "SIN_ARREGLO" || orden.estado === "RECHAZADO";
}

export interface CuentaParaCobrar {
  id: number;
  moneda: Moneda;
  /** Para saber de que cajon sale el vuelto: el de Efectivo en pesos. */
  tipo?: string;
  activo?: boolean;
}

/**
 * Lo que se le factura al cliente en ESTA entrega, antes de descontar lo que ya
 * pago.
 *
 * El equipo que vuelve sin repararse factura el DIAGNOSTICO y nada mas: es la
 * venta de servicio del local, y el cliente ya la pago al dejar el equipo, asi
 * que el comprobante nace cancelado y no hay nada que cobrar en el mostrador.
 */
export function precioEntrega(orden: OrdenTaller, f: FormularioEntrega): number {
  if (vuelveSinReparar(orden, f)) {
    return f.cobrarDiagnostico ? aNumero(orden.diagnostico_importe) : 0;
  }
  return leerPlata(orden).precio ?? 0;
}

/**
 * En que moneda se factura la entrega. El equipo que vuelve sin repararse
 * factura el diagnostico en SU moneda (`entregar_orden` usa
 * `diagnostico_moneda`); la pantalla usaba la de los costos, y con el
 * presupuesto en pesos los US$ 20 del diagnostico se mostraban y se proponian
 * como $ 20.
 */
export function monedaEntrega(orden: OrdenTaller, f: FormularioEntrega): Moneda {
  if (vuelveSinReparar(orden, f)) {
    return orden.diagnostico_moneda ?? orden.costos_moneda ?? "USD";
  }
  return orden.costos_moneda ?? "USD";
}

export interface EvaluacionEntrega {
  moneda: Moneda;
  precio: number;
  senas: number;
  /** Precio menos señas: lo que falta cobrar hoy. */
  resta: number;
  /**
   * Lo cobrado a cuenta que esta factura no usa (una seña mayor que el
   * diagnostico, en un equipo que vuelve sin arreglo). Queda a favor del
   * cliente; antes se descartaba en silencio y la pantalla no lo mostraba.
   */
  sobrante: number;
  /** Lo que se esta cobrando hoy, llevado a la moneda de la orden. */
  cobrado: number;
  /** Resta menos cobrado. Positivo: queda debiendo. Negativo: se cobro de mas. */
  pendiente: number;
  /** Queda en cuenta corriente. Solo posible con cliente cargado. */
  aCuenta: boolean;
  /**
   * Los pesos que salen de vuelto: sin cliente, lo cobrado de mas. Siempre en
   * pesos del cajon de Efectivo en pesos, como en la venta (0121_1). Cero si
   * no hay vuelto.
   */
  vuelto: number;
  /** De que caja sale el vuelto. `null` si no hay vuelto. */
  cajaVuelto: CuentaParaCobrar | null;
  /**
   * Ya no se usa: desde 0155 lo cobrado de mas es vuelto tambien con cliente,
   * como en la venta (0145). Queda en cero.
   */
  excedenteACuenta: number;
  puede: boolean;
  motivo: string | null;
}

/**
 * Las reglas de `entregar_orden`, para que la pantalla las diga antes del
 * clic: sin cliente cargado se cobra entero; con cliente, lo que falte queda
 * en su cuenta corriente.
 */
export function evaluarEntrega(
  orden: OrdenTaller,
  f: FormularioEntrega,
  cuentas: ReadonlyArray<CuentaParaCobrar>,
  cotizacion: number,
): EvaluacionEntrega {
  const moneda = monedaEntrega(orden, f);
  const precio = precioEntrega(orden, f);
  // Las señas en la moneda de la factura, congeladas el dia que entraron.
  const cobradoAntes = aNumero(moneda === "USD" ? orden.senas_usd : orden.senas_ars);
  const senas = redondear2(Math.min(cobradoAntes, precio));
  const resta = redondear2(Math.max(precio - senas, 0));
  const sobra = redondear2(cobradoAntes - senas);
  const sobrante = sobra > 0.01 ? sobra : 0;
  const conCliente = orden.cliente_id !== null && orden.cliente_id !== undefined;
  // El saldo a favor paga aunque no entre a ninguna caja. Solo con cliente, y
  // nunca mas de lo que resta: no se devuelve en plata.
  const saldoUsado = conCliente ? redondear2(Math.min(Math.max(f.saldoAFavor ?? 0, 0), resta)) : 0;
  const validos = f.pagos.filter((p) => p.importe !== null && p.importe > 0);
  const cobrado = redondear2(
    validos.reduce((acc, p) => {
      const cuenta = cuentas.find((c) => c.id === p.cuenta_id);
      if (!cuenta) return acc;
      return acc + convertir(p.importe ?? 0, cuenta.moneda, moneda, cotizacion);
    }, 0) + saldoUsado,
  );
  const pendiente = redondear2(resta - cobrado);
  // Una reparacion en pesos cobrada con dolares no llega exacta: el resto de
  // hasta medio centavo de dolar la cierra, como en la venta y en el cobro
  // (0121_8, `evaluarCobro`). La tolerancia era un centavo fijo, y los
  // US$ 58,87 propuestos para $ 93.600 decian «Se está cobrando $ 3,30 de
  // más» y frenaban la entrega.
  //
  // El corte es el de la base: `entregar_orden` cierra (y no asienta vuelto)
  // mientras la diferencia no pase el grano mas 0,009. Aca se cortaba en «menos
  // de un centavo»: US$ 56 cobrados con $ 89.055 son US$ 56,01, y la pantalla
  // anunciaba «Vuelto: $ 15» que la base no grababa.
  const grano = validos.reduce((g, p) => {
    const cuenta = cuentas.find((c) => c.id === p.cuenta_id);
    if (!cuenta || cuenta.moneda === moneda) return g;
    return Math.max(g, convertir(0.005, cuenta.moneda, moneda, cotizacion));
  }, 0.01);
  const cerrado = Math.abs(pendiente) <= grano + 0.009;
  const aCuenta = !cerrado && pendiente > 0 && conCliente;
  const base = {
    moneda,
    precio,
    senas,
    resta,
    sobrante,
    cobrado,
    pendiente,
    aCuenta: false,
    vuelto: 0,
    cajaVuelto: null,
    excedenteACuenta: 0,
  };

  // Un pago que no se entiende no se descarta en silencio: frena la entrega.
  if (f.pagos.some((p) => p.importe === null)) {
    return { ...base, puede: false, motivo: "Hay un pago sin importe: completalo o quitalo." };
  }
  if (!cerrado && pendiente > 0 && !conCliente) {
    return {
      ...base,
      puede: false,
      motivo: `Sin cliente cargado se cobra entero al entregar. Faltan ${formatear(pendiente, moneda)}.`,
    };
  }
  // (0155) Sin cliente, la seña de mas tambien se devuelve: `entregar_orden`
  // ya no la deja en la caja con la factura cobrada de mas.
  const senaDeMas = !conCliente && sobrante > 0;
  if ((!cerrado && pendiente < 0) || senaDeMas) {
    // EL VUELTO, con o sin cliente (0155, como la venta en 0145): siempre en
    // pesos, del cajon de Efectivo en pesos (el que se toco en el pago o, si
    // no, el primero activo), contado sobre los pesos que entraron. Es la
    // cuenta de `entregar_orden`. Antes, con cliente, lo de mas quedaba a
    // favor en su cuenta y el cajero daba el vuelto igual.
    const esCajonPesos = (c: CuentaParaCobrar) =>
      c.tipo === "CAJA" && c.moneda === "ARS" && c.activo !== false;
    const tocado = validos
      .map((p) => cuentas.find((c) => c.id === p.cuenta_id))
      .filter((c): c is CuentaParaCobrar => c !== undefined && esCajonPesos(c))
      .pop();
    const caja = tocado ?? [...cuentas].filter(esCajonPesos).sort((x, y) => x.id - y.id)[0] ?? null;
    const entroEnPesos = validos.reduce((acc, p) => {
      const cuenta = cuentas.find((c) => c.id === p.cuenta_id);
      return cuenta ? acc + convertir(p.importe ?? 0, cuenta.moneda, "ARS", cotizacion) : acc;
    }, 0);
    // Lo que faltaba de verdad: el precio menos las señas (todas, aunque pasen
    // el precio) y el saldo a favor usado, que tambien pago.
    const faltaba = senaDeMas ? -sobrante : Math.max(resta - saldoUsado, 0);
    const vuelto = redondear2(entroEnPesos - convertir(faltaba, moneda, "ARS", cotizacion));
    if (vuelto <= 0.009) return { ...base, aCuenta, puede: true, motivo: null };
    if (!caja) {
      return {
        ...base,
        vuelto,
        puede: false,
        motivo:
          "No hay una caja de Efectivo en pesos activa para dar el vuelto: el vuelto siempre sale en pesos. Activala en Caja o cobrá justo.",
      };
    }
    return { ...base, vuelto, cajaVuelto: caja, puede: true, motivo: null };
  }
  return { ...base, aCuenta, puede: true, motivo: null };
}

export function armarEntrega(orden: OrdenTaller, f: FormularioEntrega): EntregaOt {
  // Interna: no cobra nada. Viaja el cierre si volvio sin arreglo, y el precio
  // nuevo solo si se escribio (y solo si volvio arreglado: sin arreglo el
  // costo no cambia y el precio tampoco).
  if (esInterna(orden)) {
    if (vuelveSinReparar(orden, f)) return { cierre: "SIN_ARREGLO" };
    return f.precioVenta !== null ? { precio_venta: f.precioVenta } : {};
  }

  const pagos = f.pagos
    .filter((p): p is PagoEntrega & { importe: number } => p.importe !== null && p.importe > 0)
    .map((p) => ({
      cuenta_id: p.cuenta_id,
      medio: p.medio as MedioPago,
      importe: p.importe,
      // (0155) El cheque viaja: `entregar_orden` lo guarda en cartera.
      ...((p.medio === "CHEQUE" || p.medio === "ECHEQ") && p.cheque
        ? { cheque: { ...p.cheque, tipo: p.medio as "CHEQUE" | "ECHEQ" } }
        : {}),
    }));

  // El saldo a favor viaja solo si se eligio, y solo con cliente (0121_9f).
  const conCliente = orden.cliente_id !== null && orden.cliente_id !== undefined;
  const saldo = conCliente && (f.saldoAFavor ?? 0) > 0.009 ? redondear2(f.saldoAFavor ?? 0) : 0;
  const conSaldo = saldo > 0 ? { saldo_a_favor: saldo } : {};

  const conPropina = f.vueltoPropina ? { vuelto_propina: true } : {};

  if (!vuelveSinReparar(orden, f)) return { pagos, ...conSaldo, ...conPropina };

  // Ya estaba cerrada antes de abrir la entrega: el motivo es el que tiene.
  const cierre: MotivoCierreOt =
    f.cierre ?? (orden.estado === "RECHAZADO" ? "RECHAZADO" : "SIN_ARREGLO");
  return { pagos, cierre, cobrar_diagnostico: f.cobrarDiagnostico, ...conSaldo, ...conPropina };
}

// ── El cierre de una interna ─────────────────────────────────────────────────

export interface CierreInterno {
  /** La moneda del costo del usado: ahi se suma. */
  moneda: Moneda;
  costoAnterior: number;
  /** Tecnico + encomienda, convertidos a `moneda` al cambio de hoy. Cero sin arreglo. */
  sumado: number;
  costoNuevo: number;
  precioMoneda: Moneda;
  precioActual: number;
  /** Lo tipeado, o el actual si no se toco. */
  precioNuevo: number;
  /** Precio nuevo menos costo nuevo, en `moneda`. Puede dar negativo, y se muestra. */
  margenNuevo: number;
  sinArreglo: boolean;
  /** El usado ya no tiene stock: se vendio antes de que volviera. */
  yaVendido: boolean;
}

/** Lo que va a pasar con el usado al cerrar, para verlo antes de apretar. */
export function evaluarCierreInterno(
  orden: OrdenTaller,
  producto: Producto,
  f: FormularioEntrega,
  cotizacion: number,
): CierreInterno {
  const plata = leerPlata(orden);
  const sinArreglo = vuelveSinReparar(orden, f);
  const moneda = producto.costo_moneda;
  const costoAnterior = aNumero(producto.costo);
  const costoOrden = redondear(plata.encomienda + plata.costoTecnico);
  const sumado = sinArreglo
    ? 0
    : redondear(convertir(costoOrden, plata.moneda, moneda, cotizacion));
  const costoNuevo = redondear(costoAnterior + sumado);
  const precioActual = aNumero(producto.precio_venta);
  const precioNuevo = f.precioVenta ?? precioActual;
  const margenNuevo = redondear(
    convertir(precioNuevo, producto.precio_moneda, moneda, cotizacion) - costoNuevo,
  );
  return {
    moneda,
    costoAnterior,
    sumado,
    costoNuevo,
    precioMoneda: producto.precio_moneda,
    precioActual,
    precioNuevo,
    margenNuevo,
    sinArreglo,
    yaVendido: aNumero(producto.stock_actual) <= 0,
  };
}

/**
 * Con que arranca la entrega.
 *
 * El diagnostico se cobra por defecto: el trabajo de abrir el equipo y decir
 * que tiene ya se hizo, aunque despues no se repare.
 */
export function entregaInicial(orden: OrdenTaller): FormularioEntrega {
  const cerrada =
    orden.estado === "SIN_ARREGLO" || orden.estado === "RECHAZADO" ? orden.estado : null;
  return {
    pagos: [],
    cierre: cerrada as MotivoCierreOt | null,
    cobrarDiagnostico: true,
    precioVenta: null,
  };
}

// ── El anticipo ──────────────────────────────────────────────────────────────

/**
 * Cuanto pedirle al cliente para arrancar la reparacion.
 *
 * El anticipo es el 60% del presupuesto CONTANDO lo que ya pago. Sumarlo aparte
 * dejaba al cliente pagando casi el 80% antes de que el equipo se tocara: los
 * USD 20 del diagnostico ya son plata suya puesta en este trabajo.
 *
 * Devuelve lo que falta para llegar a ese 60%, en la moneda del presupuesto.
 * Cero significa que con lo ya pago alcanza.
 */
export function anticipoSugerido(orden: OrdenTaller, pct: number): number {
  const { precio, senas, moneda } = leerPlata(orden);
  if (!precio || precio <= 0) return 0;
  const objetivo = redondear2((precio * pct) / 100);
  void moneda;
  return redondear2(Math.max(objetivo - senas, 0));
}

// ─────────────────────────────────────────────────────────────────────────────
// El historial del equipo
// ─────────────────────────────────────────────────────────────────────────────

/** Un momento en la vida de la orden, para la linea de tiempo de la ficha. */
export interface Momento {
  clave: "recibido" | "costos" | "sin_arreglo" | "rechazado" | "tecnico" | "entregado";
  titulo: string;
  detalle: string | null;
  fecha: string;
}

/**
 * La linea de tiempo de ESTA orden, con las fechas que la vista trae. Se
 * escribe con las palabras del flujo real (recibido, costos, entregado) y no
 * con los nueve estados de la base.
 */
export function momentosDe(orden: OrdenTaller): Momento[] {
  const m: Momento[] = [
    {
      clave: "recibido",
      titulo: "Entró al taller",
      detalle: orden.falla_declarada,
      fecha: orden.fecha_ingreso,
    },
  ];
  if (orden.costos_at) {
    const plata = leerPlata(orden);
    m.push({
      clave: "costos",
      titulo: "Contestó el técnico",
      detalle:
        (orden.diagnostico ? `${orden.diagnostico} · ` : "") +
        // La interna no tiene precio al cliente: lo que se carga es lo que
        // suma al costo del equipo, y el historial decia «precio».
        (plata.precio !== null
          ? `${esInterna(orden) ? "costo" : "precio"} ${formatear(plata.precio, plata.moneda)}`
          : ""),
      fecha: orden.costos_at,
    });
  }
  // Como volvio, tambien despues de entregada: el momento desaparecia al
  // entregar, y un rechazo no figuraba nunca. La vista no trae `rechazado_at`:
  // va con la fecha en que contesto el tecnico, que es cuando se supo.
  const cierre =
    orden.motivo_cierre ??
    (orden.estado === "SIN_ARREGLO" || orden.estado === "RECHAZADO" ? orden.estado : null);
  if (cierre === "SIN_ARREGLO") {
    m.push({
      clave: "sin_arreglo",
      titulo: "Volvió sin arreglo",
      detalle: null,
      fecha: orden.costos_at ?? orden.fecha_ingreso,
    });
  } else if (cierre === "RECHAZADO") {
    m.push({
      clave: "rechazado",
      titulo: "El cliente no lo aprobó",
      detalle: null,
      fecha: orden.costos_at ?? orden.fecha_ingreso,
    });
  }
  if (orden.pago_tecnico_at) {
    const plata = leerPlata(orden);
    // Lo que salió de la caja, no lo cotizado (ver `pagoAlTecnico`).
    const pagado = pagoAlTecnico(orden) ?? plata.costoTecnico;
    m.push({
      clave: "tecnico",
      titulo: "Se le pagó al técnico",
      detalle: pagado > 0 ? formatear(pagado, plata.moneda) : null,
      fecha: orden.pago_tecnico_at,
    });
  }
  if (orden.fecha_entrega) {
    // Lo cobrado a cuenta sin factura (la seña de una rechazada) no es «sin
    // cobrar»: se dice cuanto quedo.
    const aCuenta = orden.comprobante ? 0 : leerPlata(orden).senas;
    const sinFactura = esInterna(orden) ? null : cierre ? "se devolvió sin facturar" : "sin cobrar";
    m.push({
      clave: "entregado",
      // La interna no se entrega a nadie: vuelve al stock.
      titulo: esInterna(orden) ? "Volvió al stock" : "Se entregó",
      // La garantia va aca aunque no haya comprobante: es el unico lugar donde
      // se la ve desde que la ficha dejo de tener una tarjeta "La entrega" que
      // repetia esta misma linea dos bloques mas abajo.
      detalle: [
        orden.comprobante ?? sinFactura,
        aCuenta > 0.009
          ? `quedaron ${formatear(aCuenta, leerPlata(orden).moneda)} cobrados a cuenta`
          : null,
        // Solo lo reparado lleva garantia: una sin arreglo o interna vieja
        // pudo quedar con la fecha grabada de antes de 0131, y decia
        // «garantía hasta…» sobre un equipo que volvio como vino.
        orden.garantia_hasta && llevaGarantia(orden)
          ? `garantía hasta el ${formatearFechaCorta(orden.garantia_hasta)}`
          : null,
      ]
        .filter(Boolean)
        .join(" · "),
      fecha: orden.fecha_entrega,
    });
  }
  // En orden de fecha: el pago al tecnico puede ser antes o despues de la
  // entrega. `sort` es estable: los de la misma fecha quedan como se armaron.
  return m.sort((a, b) => new Date(a.fecha).getTime() - new Date(b.fecha).getTime());
}

function formatearFechaCorta(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("es-AR", { day: "2-digit", month: "2-digit", year: "numeric" });
}

function imeiNormalizado(imei: string | null | undefined): string {
  return (imei ?? "").replace(/\D/g, "");
}

/**
 * Las otras veces que vino ESTE equipo.
 *
 * El IMEI es la identidad: si coincide, es el mismo telefono aunque el
 * cliente lo haya cargado con otro nombre. Sin IMEI se cae a "mismo cliente y
 * mismo equipo escrito igual", que es lo mejor que hay. Ordenado del mas
 * reciente al mas viejo, sin la orden que se esta mirando.
 */
export function historialDelEquipo(
  ordenes: ReadonlyArray<OrdenTaller>,
  orden: OrdenTaller,
): OrdenTaller[] {
  const imei = imeiNormalizado(orden.imei);
  const mismo = (o: OrdenTaller): boolean => {
    if (o.id === orden.id) return false;
    if (imei.length >= 8) return imeiNormalizado(o.imei) === imei;
    return (
      orden.cliente_id !== null &&
      orden.cliente_id !== undefined &&
      o.cliente_id === orden.cliente_id &&
      normalizar(o.equipo) === normalizar(orden.equipo)
    );
  };
  return ordenes
    .filter(mismo)
    .sort((a, b) => new Date(b.fecha_ingreso).getTime() - new Date(a.fecha_ingreso).getTime());
}
